
// renderer.js — Three.js presentation layer for Token Storm: the HDR bloom / tonemap composite chain (PostFX),
// one coherent machine (renderer.machineRoot: pitched playfield group with its mechanisms, cabinet body, legs,
// backbox on a neck with translite + DMD, glass, plunger housing), the room around it, instanced lamp inserts and
// glows, particle bursts, aspect-fitted camera views, screen shake and the orbiting "View Machine" viewer.
// Depends on: three.min.js (THREE), table.js (TABLE), art.js (paintPlayfield, paintRoughness, paintSideArt,
// paintTranslite, makeBlobTexture, MiniScreen, lampColor) and assets.js (XARCADE_LOGO_PNG).
// The renderer never mutates physics state. It only consumes Math.random() inside burst(), exactly as the original
// bundle did, so the deterministic replay tests keep their random sequence.
//
// Public surface used by other modules: new Renderer(canvas, T, lamps); render(dt, world, game, alpha, prev);
// resize(w, h); handleFx(fx); cycleView(); view; fw(x, y, z, out); camera; dmdCanvas; dmdTex; machineRoot;
// setMachineView(active); machineView = { active, yaw, pitch, radius }; overlayElements (any DOM node added to this
// Set is hidden while the machine view is active; main.js already switches the debug overlay off); dispose().

/** Vertex shader shared by every fullscreen pass: the triangle's positions are already in clip space. */
const POST_VERTEX_SHADER = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

/** One oversized triangle covering the whole viewport (no diagonal seam, fewer vertices than a quad). */
// Rounded sheet-metal/plastic panel. Its quoted dimensions remain the outer footprint.
function roundedPanelGeometry(w,h,d,r=.003,bevel=0) {
  w-=2*bevel;h-=2*bevel;
  const x=-w/2,y=-h/2,shape=new THREE.Shape();r=Math.min(r,w/3,h/3);
  shape.moveTo(x+r,y);shape.lineTo(x+w-r,y);shape.quadraticCurveTo(x+w,y,x+w,y+r);
  shape.lineTo(x+w,y+h-r);shape.quadraticCurveTo(x+w,y+h,x+w-r,y+h);
  shape.lineTo(x+r,y+h);shape.quadraticCurveTo(x,y+h,x,y+h-r);
  shape.lineTo(x,y+r);shape.quadraticCurveTo(x,y,x+r,y);
  const geo=new THREE.ExtrudeGeometry(shape,{depth:d-2*bevel,bevelEnabled:bevel>0,bevelSize:bevel,bevelThickness:bevel,bevelSegments:3,curveSegments:5});geo.translate(0,0,-d/2+bevel);return geo;
}

function fullscreenTriangle(material) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));
  return new THREE.Mesh(geometry, material);
}

/**
 * Post-processing chain: scene -> half-float target -> bright pass -> two half-res gaussian iterations (bloom1)
 * -> one quarter-res iteration (bloom2) -> composite (chromatic aberration, bloom, exposure, ACES, vignette, grain,
 * gamma). The WebGLRenderer itself runs with NoToneMapping / linear output because this shader does the tonemap.
 */
class PostFX {
  constructor(renderer) {
    this.renderer = renderer;
    this.scene = new THREE.Scene();
    this.cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const sceneTargetOpts = {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
    };
    const blurTargetOpts = { ...sceneTargetOpts, depthBuffer: false };
    this.sceneRT = new THREE.WebGLRenderTarget(2, 2, sceneTargetOpts);
    this.brightRT = new THREE.WebGLRenderTarget(2, 2, blurTargetOpts);
    this.blurA = new THREE.WebGLRenderTarget(2, 2, blurTargetOpts);
    this.blurB = new THREE.WebGLRenderTarget(2, 2, blurTargetOpts);
    this.blurC = new THREE.WebGLRenderTarget(2, 2, blurTargetOpts);
    this.blurD = new THREE.WebGLRenderTarget(2, 2, blurTargetOpts);
    // Bright pass: soft-knee threshold on the max channel so only emissive/neon detail feeds the bloom.
    this.brightMat = new THREE.ShaderMaterial({
      uniforms: { tex: { value: null }, threshold: { value: 1.18 }, knee: { value: 0.55 } },
      vertexShader: POST_VERTEX_SHADER,
      fragmentShader: `
        uniform sampler2D tex; uniform float threshold; uniform float knee;
        varying vec2 vUv;
        void main() {
          vec3 c = texture2D(tex, vUv).rgb;
          float l = max(c.r, max(c.g, c.b));
          float w = smoothstep(threshold - knee, threshold + knee, l);
          gl_FragColor = vec4(c * w, 1.0);
        }`,
      depthTest: false,
      depthWrite: false,
    });
    // Separable 5-tap gaussian using linear-sampling offsets (1.3846 / 3.2307).
    this.blurMat = new THREE.ShaderMaterial({
      uniforms: {
        tex: { value: null },
        dir: { value: new THREE.Vector2(1, 0) },
        texel: { value: new THREE.Vector2() },
      },
      vertexShader: POST_VERTEX_SHADER,
      fragmentShader: `
        uniform sampler2D tex; uniform vec2 dir; uniform vec2 texel;
        varying vec2 vUv;
        void main() {
          vec2 o = dir * texel;
          vec3 c = texture2D(tex, vUv).rgb * 0.227027;
          c += (texture2D(tex, vUv + o * 1.3846).rgb + texture2D(tex, vUv - o * 1.3846).rgb) * 0.3162162;
          c += (texture2D(tex, vUv + o * 3.2307).rgb + texture2D(tex, vUv - o * 3.2307).rgb) * 0.0702703;
          gl_FragColor = vec4(c, 1.0);
        }`,
      depthTest: false,
      depthWrite: false,
    });
    // Composite. bloomStrength 0.34 / exposure 1.12 are the values the shipped build runs with (the bundle's
    // own default was bloomStrength 0.5, retuned at runtime to stop the lamp halos from clipping).
    this.compMat = new THREE.ShaderMaterial({
      uniforms: {
        tex: { value: null },
        bloom1: { value: null },
        bloom2: { value: null },
        bloomStrength: { value: 0.22 },
        time: { value: 0 },
        vignette: { value: 0.10 },
        exposure: { value: 1.12 },
      },
      vertexShader: POST_VERTEX_SHADER,
      fragmentShader: `
        uniform sampler2D tex; uniform sampler2D bloom1; uniform sampler2D bloom2;
        uniform float bloomStrength; uniform float time; uniform float vignette; uniform float exposure;
        varying vec2 vUv;
        vec3 aces(vec3 x) {
          const float a = 2.51; const float b = 0.03;
          const float c = 2.43; const float d = 0.59; const float e = 0.14;
          return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
        }
        float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        void main() {
          vec2 uv = vUv;
          vec2 d = uv - 0.5;
          // subtle chromatic aberration toward edges
          float ca = dot(d, d) * 0.0005;
          vec3 col;
          col.r = texture2D(tex, uv + d * ca).r;
          col.g = texture2D(tex, uv).g;
          col.b = texture2D(tex, uv - d * ca).b;
          vec3 bl = texture2D(bloom1, uv).rgb * 0.72 + texture2D(bloom2, uv).rgb * 0.55;
          col += bl * bloomStrength;
          col *= exposure;
          col = aces(col);
          // vignette
          float v = smoothstep(0.92, 0.30, length(d) * (1.0 + vignette));
          col *= mix(0.88, 1.0, v);
          // grain
          col += (hash(uv * vec2(1920.0, 1080.0) + time) - 0.5) * 0.001;
          gl_FragColor = vec4(pow(max(col, 0.0), vec3(1.0 / 2.2)), 1.0);
        }`,
      depthTest: false,
      depthWrite: false,
    });
    this.quad = fullscreenTriangle(this.brightMat);
    this.scene.add(this.quad);
  }

  /** Resize all targets: scene at full size, first blur chain at 1/2, wide blur chain at 1/4. */
  setSize(width, height) {
    this.sceneRT.setSize(width, height);
    this.brightRT.setSize(width >> 1, height >> 1);
    this.blurA.setSize(width >> 1, height >> 1);
    this.blurB.setSize(width >> 1, height >> 1);
    this.blurC.setSize(width >> 2, height >> 2);
    this.blurD.setSize(width >> 2, height >> 2);
  }

  /** Run one fullscreen material into a target (null = the canvas). */
  pass(material, target) {
    this.quad.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.scene, this.cam);
  }

  /** Render the 3D scene through the whole chain onto the canvas. `time` seeds the film grain. */
  render(scene, camera, time) {
    const gl = this.renderer;
    gl.setRenderTarget(this.sceneRT);
    gl.render(scene, camera);
    this.brightMat.uniforms.tex.value = this.sceneRT.texture;
    this.pass(this.brightMat, this.brightRT);
    // Two ping-pong gaussian iterations at half resolution -> tight bloom (blurB).
    let source = this.brightRT;
    for (let iteration = 0; iteration < 2; iteration++) {
      this.blurMat.uniforms.texel.value.set(1 / this.blurA.width, 1 / this.blurA.height);
      this.blurMat.uniforms.tex.value = source.texture;
      this.blurMat.uniforms.dir.value.set(1, 0);
      this.pass(this.blurMat, this.blurA);
      this.blurMat.uniforms.tex.value = this.blurA.texture;
      this.blurMat.uniforms.dir.value.set(0, 1);
      this.pass(this.blurMat, this.blurB);
      source = this.blurB;
    }
    // One more iteration at quarter resolution -> wide halo (blurD).
    this.blurMat.uniforms.texel.value.set(1 / this.blurC.width, 1 / this.blurC.height);
    this.blurMat.uniforms.tex.value = this.blurB.texture;
    this.blurMat.uniforms.dir.value.set(1, 0);
    this.pass(this.blurMat, this.blurC);
    this.blurMat.uniforms.tex.value = this.blurC.texture;
    this.blurMat.uniforms.dir.value.set(0, 1);
    this.pass(this.blurMat, this.blurD);
    this.compMat.uniforms.tex.value = this.sceneRT.texture;
    this.compMat.uniforms.bloom1.value = this.blurB.texture;
    this.compMat.uniforms.bloom2.value = this.blurD.texture;
    this.compMat.uniforms.time.value = time % 1e3;
    gl.setRenderTarget(null);
    this.pass(this.compMat, null);
  }

  /** Free the six render targets and the three pass materials. */
  dispose() {
    const targets = [this.sceneRT, this.brightRT, this.blurA, this.blurB, this.blurC, this.blurD];
    for (const target of targets) target.dispose();
    this.brightMat.dispose();
    this.blurMat.dispose();
    this.compMat.dispose();
    this.quad.geometry.dispose();
  }
}

/** Playfield pitch in radians (rotation about the table group's X axis; positive tilts the far end up). */
const TABLE_TILT = 0.115;
/** Height of the playfield surface at the table's centre above the floor (metres). */
const TABLE_HEIGHT = 0.86;
/** Height of the glass above the playfield surface; every playfield mechanism must stay below it. */
const GLASS_Z = 0.102;
/** Cabinet wall thickness and how far the side walls / front and rear panels stand off the playfield edge. */
const CAB_WALL = 0.024;
const CAB_SIDE_X = TABLE.W / 2 + 0.018; // centre line of a side wall
const CAB_FRONT_Z = TABLE.L / 2 + 0.055; // centre line of the front panel (table-group space, +Z = player)
const CAB_REAR_Z = -TABLE.L / 2 - 0.04; // centre line of the rear panel
// Cabinet side top above the playfield: a few mm proud of the glass, so the side rails carry it the way a real
// machine's do and nothing on the playfield (the ramp apex included) stands above the cabinet silhouette.
const CAB_TOP_Y = GLASS_Z + 0.006;
const CAB_BOTTOM_Y = -0.315;
/** Backbox dimensions (world space, upright). */
const BACKBOX_W = TABLE.W + 0.06;
const BACKBOX_H = 0.46;
const BACKBOX_D = 0.14;
const NECK_H = 0.05;

/**
 * Table coordinates -> table-group space. Physics uses x to the right (0..TABLE.W) and y up the playfield
 * (0 = flipper end, TABLE.L = back wall); the table group is centred with +Z pointing toward the player.
 * `z` is height above the playfield surface.
 */
const toWorld = (x, y, z = 0) => new THREE.Vector3(x - TABLE.W / 2, z, TABLE.L / 2 - y);

/**
 * Bake many small geometries (each with an optional Matrix4) into one non-indexed BufferGeometry so the hundreds of
 * rail pieces draw as a single mesh. Parts without UVs get (0,0) so the attribute counts stay consistent.
 * The source geometries are disposed once copied.
 */
function mergeGeometries(parts) {
  const positions = [];
  const normals = [];
  const uvs = [];
  for (const { geo, m } of parts) {
    const flat = geo.index ? geo.toNonIndexed() : geo;
    if (m) flat.applyMatrix4(m);
    positions.push(...flat.getAttribute('position').array);
    normals.push(...flat.getAttribute('normal').array);
    const uv = flat.getAttribute('uv');
    if (uv) uvs.push(...uv.array);
    else for (let i = 0; i < flat.getAttribute('position').count; i++) uvs.push(0, 0);
    if (flat !== geo) flat.dispose();
    geo.dispose();
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  merged.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return merged;
}

/**
 * Turn the supplied X-Arcade logo (black lettering and a red/grey X on pure white) into a lit sign sprite:
 * white -> transparent (alpha = 1 - min(r,g,b)/255), the lettering's own colour is recovered by un-premultiplying
 * against white so red/grey keep their hue, and near-black pixels become white. Returns the sprite canvas plus
 * the bounding box of its opaque content so callers can letterbox the lettering instead of the image's margins.
 */
function litLogoSprite(image) {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, width, height);
  const pixels = ctx.getImageData(0, 0, width, height);
  const data = pixels.data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const alpha = 1 - Math.min(r, g, b) / 255;
    if (alpha < 0.03) {
      data[i + 3] = 0;
      continue;
    }
    // Source = colour * alpha + white * (1 - alpha): recover the colour under the anti-aliased edge.
    const white = (1 - alpha) * 255;
    let cr = Math.max(0, Math.min(255, (r - white) / alpha));
    let cg = Math.max(0, Math.min(255, (g - white) / alpha));
    let cb = Math.max(0, Math.min(255, (b - white) / alpha));
    if (Math.max(cr, cg, cb) < 70) cr = cg = cb = 255; // black lettering reads as a lit white sign
    data[i] = cr;
    data[i + 1] = cg;
    data[i + 2] = cb;
    data[i + 3] = Math.round(alpha * 255);
    const x = (i / 4) % width;
    const y = (i / 4 - x) / width;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  ctx.putImageData(pixels, 0, 0);
  const box =
    maxX < 0 ? { x: 0, y: 0, w: width, h: height } : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  return { canvas, box };
}

/**
 * Renderer: owns the WebGLRenderer, the scene graph, the post chain and every animated visual.
 * `T` is the built table ({world, lamps, meta, ramps}), `lamps` the rules-side Lamps instance (brightness/gi).
 */
class Renderer {
  constructor(canvas, T, lamps) {
    this.T = T;
    this.lamps = lamps;
    // Embedded startup must not force a discrete-GPU switch for the entire browser.
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: window.parent === window, powerPreference: 'default' });
    // Tonemapping + gamma happen in PostFX.compMat, so the renderer stays linear.
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x030308);
    // Fog reaches past the room's walls (2.6 m) so the floor and the back wall still read behind the machine;
    // the machine viewer pushes it out further because it orbits away from the cabinet.
    this.scene.fog = new THREE.Fog(0x030308, 2.4, 8.5);
    this.fogPlay = { near: 2.4, far: 8.5 };
    this.fogMachine = { near: 3.4, far: 12 };
    this.camera = new THREE.PerspectiveCamera(40, 16 / 9, 0.05, 14);
    this.post = new PostFX(this.renderer);
    this.time = 0;
    this.uptime = 0; // seconds since construction (renderScale ignores the shader-compile stalls of the first 2 s)
    this.renderScale = 1; // adaptive resolution factor (0.68..1), see updateRenderScale()
    this.frameSamples = new Float32Array(48); // ring of frame intervals feeding the renderScale decision
    this.frameSampleCount = 0;
    this.displayInterval = 1 / 30; // shortest frame interval seen so far = the display's refresh period
    this.scaleChangedAt = 0;
    this.shake = { mag: 0, t: 0, dx: 0, dy: 0 };
    this.view = 0; // 0 standard, 1 ball-follow, 2 top-down, 3 low wide (cycleView)
    this.camPos = new THREE.Vector3();
    this.camTgt = new THREE.Vector3();
    this.goalPos = new THREE.Vector3();
    this.goalTgt = new THREE.Vector3();
    this.tmpV = new THREE.Vector3();
    this.tmpV2 = new THREE.Vector3();
    this.tmpDir = new THREE.Vector3();
    this.tmpRight = new THREE.Vector3();
    this.tmpUp = new THREE.Vector3();
    this.tmpOrbit = new THREE.Vector3(); // attract: the eased view direction (never a scratch of fitDistance)
    // frameFit() results, reused every frame: { distance, ox, oy, ax, bx, ay, by, tanH, tanV }.
    this.fitPlayfieldOnly = { distance: 0, ox: 0, oy: 0, ax: 0, bx: 0, ay: 0, by: 0, tanH: 0, tanV: 0 };
    this.fitWithDmd = { distance: 0, ox: 0, oy: 0, ax: 0, bx: 0, ay: 0, by: 0, tanH: 0, tanV: 0 };
    this.tmpColor = new THREE.Color();
    this.tmpColor2 = new THREE.Color();
    this.liveIds = new Set();
    this.ballMeshes = new Map(); // ball id -> { mesh, blob }
    this.ballPool = []; // released ball visuals, reused so despawns allocate nothing
    this.dropAnim = [0, 0, 0]; // 0 = target up, 1 = target down (eased)
    this.spinnerRot = 0;
    this.spinnerVel = 0;
    this.plungerPower = 0;
    this.gateSwing = 0;
    this.camKick = 0; // multiball camera bump (0..1)
    this.msAcc = 0; // mini-screen redraw accumulator (s)
    this.lastFrame = { world: null, game: null, alpha: 1, prev: null }; // last render() arguments (V16 redraw)
    // "View Machine": an orbit camera around machineRoot while the simulation is paused by main.js.
    this.machineView = { active: false, yaw: 0.55, pitch: 0.3, radius: 2.4 };
    this.overlayElements = new Set();
    this.machineTarget = new THREE.Vector3();
    this.machineCorners = [];
    this.pointers = new Map(); // active pointers in machine view: id -> { x, y }
    this.pinchDist = 0;
    this.savedView = null;
    this.machineRaf = 0;
    this.machineLoop = () => {
      this.machineRaf = 0;
      if (!this.machineView.active || this.hostSuspended || document.hidden) return;
      this.machineRaf = requestAnimationFrame(this.machineLoop);
      this.renderMachineFrame();
    };
    this.onPointerDown = (event) => this.machinePointerDown(event);
    this.onPointerMove = (event) => this.machinePointerMove(event);
    this.onPointerUp = (event) => this.machinePointerUp(event);
    this.onWheel = (event) => this.machineWheel(event);
    // The table group's world matrix is fixed once built: fw() reuses it instead of re-walking the scene graph.
    this.tableMatrix = new THREE.Matrix4().makeRotationX(TABLE_TILT).setPosition(0, TABLE_HEIGHT, 0);
    this.machineRoot = new THREE.Group();
    this.machineRoot.name = 'machineRoot';
    this.scene.add(this.machineRoot);
  }

  // Yield between construction phases so input and page scrolling can be serviced.
  async initialize() {
    const phases = ["buildEnvironment", "buildTableGroup", "buildCabinet", "buildRoom", "buildLampInstances", "buildParticles", "buildLights", "buildFitPoints"];
    for (let i = 0; i < phases.length; i++) {
      GameLoading.stage(32 + i * 2, "Assembling the table…");
      await GameLoading.paint();
      await this[phases[i]]();
    }
    this.resize(window.innerWidth, window.innerHeight);
    // No fly-in: start on the attract pose.
    this.computeGoal('ATTRACT', null, 0);
    this.camPos.copy(this.goalPos);
    this.camTgt.copy(this.goalTgt);
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camTgt);
  }

  /** Prepare shader variants, textures and post-processing before exposing the first playable frame. */
  async prepareForPlay(world, game, dmd, snapshot, report) {
    report(55, 'Loading artwork…');
    await this.assetsReady;
    await document.fonts?.ready;
    await GameLoading.paint();
    const textures = new Set();
    this.scene.traverse(node => {
      for (const material of (Array.isArray(node.material) ? node.material : [node.material])) {
        if (!material) continue;
        for (const value of Object.values(material)) if (value?.isTexture) textures.add(value);
      }
    });
    for (const texture of textures) {
      await GameLoading.paint();
      this.renderer.initTexture(texture);
    }
    report(70, 'Preparing lighting and reflections…');
    // Include the first ball material even though attract mode has no ball yet; return it to the pool.
    const ball = this.acquireBallVisual();
    ball.mesh.position.set(0, .03, 0);
    try {
      if (this.renderer.compileAsync) await this.renderer.compileAsync(this.scene, this.camera);
      else this.renderer.compile(this.scene, this.camera);
      report(85, 'Warming up the table…');
      for (let i = 0; i < 4; i++) {
        await GameLoading.paint();
        dmd.draw(0);
        this.dmdTex.needsUpdate = true;
        this.render(0, world, game, 1, snapshot);
      }
      // Wait for actual GPU completion, including post FX and texture uploads, not an arbitrary delay.
      const gl = this.renderer.getContext();
      if (gl.isContextLost()) throw new Error('Graphics context lost during startup');
      if (gl.fenceSync) {
        const fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
        if (!fence) throw new Error('Could not complete graphics preparation');
        gl.flush();
        const deadline = performance.now() + 30000;
        try {
          while (true) {
            const result = gl.clientWaitSync(fence, 0, 0);
            if (result === gl.WAIT_FAILED || gl.isContextLost()) throw new Error('Graphics preparation failed');
            if (result !== gl.TIMEOUT_EXPIRED) break;
            if (performance.now() > deadline) throw new Error('Graphics preparation timed out');
            await GameLoading.paint();
          }
        } finally { gl.deleteSync(fence); }
      } else gl.finish();
    } finally {
      this.table.remove(ball.mesh, ball.blob);
      this.ballPool.push(ball);
    }
    this.render(0, world, game, 1, snapshot);
    report(95, 'Finishing setup…');
    await GameLoading.paint();
  }

  /** Tiny three-panel "studio" baked through PMREM into the scene environment map (metal/clearcoat reflections). */
  buildEnvironment() {
    const studio = new THREE.Scene();
    studio.background = new THREE.Color(0x05050a);
    const addPanel = (color, width, height, x, y, z, rotY = 0) => {
      const panel = new THREE.Mesh(new THREE.PlaneGeometry(width, height), new THREE.MeshBasicMaterial({ color }));
      panel.position.set(x, y, z);
      panel.rotation.y = rotY;
      studio.add(panel);
    };
    addPanel(new THREE.Color(1.5, 0.18, 0.4), 1.2, 2.4, -2.4, 1.2, 0, Math.PI / 2); // magenta side
    addPanel(new THREE.Color(0.15, 1.1, 1.5), 1.2, 2.4, 2.4, 1.2, 0, -Math.PI / 2); // cyan side
    addPanel(new THREE.Color(1, 0.95, 0.9), 2.8, 2.8, 0, 3.4, 0, 0);
    studio.children[studio.children.length - 1].rotation.x = -Math.PI / 2; // warm top
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(studio, 0.06, 0.1, 100, {size: 64}).texture;
    pmrem.dispose();
    studio.traverse((node) => {
      if (node.isMesh) {
        node.geometry.dispose();
        node.material.dispose();
      }
    });
  }

  /**
   * Scene lights. ambient/keyLight/popLight/flipLight intensities are re-driven every frame from the GI level
   * (hemi 0.98, ambient 0.6 and key 1.1 at full GI). The pop light is a warm fixture bulb under the pop nest and
   * the flipper light a faint cyan wash from under the apron edge, low enough not to blow out the drain.
   */
  buildLights() {
    this.ambient = new THREE.AmbientLight(0xc9d1da, 0.85);
    this.hemiFill = new THREE.HemisphereLight(0xa7bccf, 0x343036, 0.85);
    this.cabGlow = new THREE.PointLight(0xffa88a, 0.22, 1.6, 2); // warm glow below the apron
    this.cabGlow.position.copy(this.fw(0.25, -0.28, 0.18));
    this.keyLight = new THREE.DirectionalLight(0xfff0dd, 1.1);
    this.keyLight.position.set(-0.6, 1.5, 0.9);
    const pops = this.T.meta.pops;
    let popX = 0;
    let popY = 0;
    for (const [x, y] of pops) {
      popX += x / pops.length;
      popY += y / pops.length;
    }
    this.popLight = new THREE.PointLight(0xffc997, 0.12, 0.50, 2);
    this.popLight.position.copy(this.fw(popX, popY, 0.05));
    // The fixture the pop light comes from: a short chrome post with an amber bulb at 5 cm.
    const fixture = new THREE.Group();
    fixture.position.copy(toWorld(popX, popY, 0));
    const stem = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0015, 0.0015, 0.045, 6),
      new THREE.MeshStandardMaterial({ color: 0xa9b2c4, metalness: 0.9, roughness: 0.3 }),
    );
    stem.position.y = 0.0225;
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.0035, 10, 8),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(1.25, 0.78, 0.42) }),
    );
    bulb.position.y = 0.05;
    fixture.add(stem, bulb);
    this.table.add(fixture);
    this.flipLight = new THREE.PointLight(0x86c9ff, 0.18, 0.5, 2);
    this.flipLight.position.copy(this.fw(0.25, -0.01, 0.05));
    this.scene.add(this.cabGlow, this.hemiFill, this.ambient, this.keyLight, this.popLight, this.flipLight);
  }

  /** The pitched table group and everything mounted on the playfield. */
  async buildTableGroup() {
    const table = this.T;
    this.table = new THREE.Group();
    this.table.name = 'table';
    this.table.rotation.x = TABLE_TILT;
    this.table.position.set(0, TABLE_HEIGHT, 0);
    this.machineRoot.add(this.table);

    // Playfield art (canvas painted by art.js) with a painted roughness map under a clearcoat.
    const artW = window.parent !== window ? 768 : 1280;
    const artH = artW * 2;
    const artTex = new THREE.CanvasTexture(paintPlayfield(artW, artH, table));
    artTex.colorSpace = THREE.SRGBColorSpace;
    artTex.anisotropy = Math.min(8, this.maxAnisotropy);
    await GameLoading.paint();
    const roughTex = new THREE.CanvasTexture(paintRoughness(artW, artH));
    const playfieldGeo = new THREE.PlaneGeometry(TABLE.W, TABLE.L);
    playfieldGeo.rotateX(-Math.PI / 2);
    // The printed art self-lights a little (emissiveMap = the same canvas): the near-black background stays dark
    // while the silkscreen lifts into a readable mid-tone, which the light rig alone could not do without
    // blowing out the lamps and the flipper bats standing on the same surface.
    this.playfieldMat = new THREE.MeshPhysicalMaterial({
      map: artTex,
      roughnessMap: roughTex,
      emissive: 0xffffff,
      emissiveMap: artTex,
      emissiveIntensity: 0.34,
      roughness: 1,
      metalness: 0.15,
      clearcoat: 0.7,
      clearcoatRoughness: 0.28,
      envMapIntensity: 0.7,
    });
    const playfield = new THREE.Mesh(playfieldGeo, this.playfieldMat);
    playfield.name = 'playfield';
    this.table.add(playfield);

    await GameLoading.paint();
    this.buildRails();
    await GameLoading.paint();

    this.buildPlayfieldPlastics();
    await GameLoading.paint();

    // Flippers: one white bat (bevelled extrude matching the collider capsule) wearing a thin red rubber band
    // around its edge; pivoted at the collider pivot and rotated in render().
    this.flippers = {};
    // Mid-grey, not white: at 0xe8e6ee the bats clipped to paper white, and with bloom that glare swallowed the
    // ball and blanked the SHOOT AGAIN insert. The specular highlight does the "white plastic" work instead.
    const batMat = new THREE.MeshStandardMaterial({
      color: 0xb6b9c2,
      metalness: 0.05,
      roughness: 0.24,
      envMapIntensity: 1.3,
    });
    const rubberMat = new THREE.MeshStandardMaterial({
      color: 0xd42035,
      roughness: 0.5,
      emissive: 0x420410,
      emissiveIntensity: 0.08,
    });
    const capsule = (reach, r0, r1) => {
      const outline = new THREE.Shape();
      outline.absarc(0, 0, r0, Math.PI / 2, -Math.PI / 2, false);
      outline.lineTo(reach, -r1);
      outline.absarc(reach, 0, r1, -Math.PI / 2, Math.PI / 2, false);
      outline.closePath();
      return outline;
    };
    for (const side of ['L', 'R', 'U']) {
      const flipper = table.meta.flippers[side];
      if (!flipper) continue;
      const group = new THREE.Group();
      group.position.copy(toWorld(flipper.pivot.x, flipper.pivot.y, 0.0135));
      const batGeo = new THREE.ExtrudeGeometry(capsule(flipper.length, flipper.r0 - 0.0044, flipper.r1 - 0.0044), {
        depth: 0.011,
        bevelEnabled: true,
        bevelThickness: 0.0012,
        bevelSize: 0.0012,
        bevelSegments: 2,
      });
      batGeo.rotateX(Math.PI / 2);
      batGeo.translate(0, 0.0055, 0);
      const bat = new THREE.Mesh(batGeo, batMat);
      // Rubber: 3.2 mm around a 76.2 mm main plastic bat, at ball height, matching the tapered collider.
      const band = capsule(flipper.length, flipper.r0, flipper.r1);
      band.holes.push(capsule(flipper.length, flipper.r0 - 0.0032, flipper.r1 - 0.0032));
      const rubberGeo = new THREE.ExtrudeGeometry(band, { depth: 0.0085, bevelEnabled: false });
      rubberGeo.rotateX(Math.PI / 2);
      rubberGeo.translate(0, 0.00425, 0);
      const rubber = new THREE.Mesh(rubberGeo, rubberMat);
      group.add(bat, rubber);
      const pin=new THREE.Mesh(new THREE.CylinderGeometry(.003,.003,.001,20),new THREE.MeshStandardMaterial({color:0x9ea9b1,metalness:.85,roughness:.27}));
      pin.position.y=.007;group.add(pin);
      const detail=new THREE.Mesh(new THREE.BoxGeometry(flipper.length*.48,.0007,.0012),new THREE.MeshStandardMaterial({color:0x646d75,roughness:.5}));
      detail.position.set(flipper.length*.5,.007,0);group.add(detail);
      this.flippers[side] = { grp: group, f: flipper };
      this.table.add(group);
    }

    // Pop bumpers modelled at the collider radius: base, red skirt, translucent lit cap, dark lid. The hit pulse
    // scales relative to `base` so a hit bumper returns to exactly this size.
    this.pops = [];
    const popBaseMat = new THREE.MeshStandardMaterial({ color: 0x222230, metalness: 0.6, roughness: 0.4 });
    const popSkirtMat = new THREE.MeshStandardMaterial({ color: 0xd42035, metalness: 0.1, roughness: 0.45 });
    const popLidMat = new THREE.MeshStandardMaterial({ color: 0x30303a, metalness: 0.7, roughness: 0.3 });
    table.meta.pops.forEach(([popX, popY], i) => {
      const body = table.world.byTag('popBody' + i);
      const radius = body && body.r ? body.r : 0.029;
      const group = new THREE.Group();
      group.position.copy(toWorld(popX, popY, 0));
      const base = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.66, radius * 0.76, 0.012, 20), popBaseMat);
      base.position.y = 0.006;
      const skirt = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.84, radius, 0.011, 24), popSkirtMat);
      skirt.position.y = 0.0145;
      const capMat = new THREE.MeshStandardMaterial({
        color: 0xff5060,
        roughness: 0.25,
        metalness: 0,
        emissive: 0xff2033,
        emissiveIntensity: 0.25,
        transparent: true,
        opacity: 0.9,
      });
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.8, radius * 0.9, 0.017, 24), capMat);
      cap.position.y = 0.0285;
      const lid = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.8, radius * 0.8, 0.004, 24), popLidMat);
      lid.position.y = 0.039;
      group.add(base, skirt, cap, lid);
      // Printed enamel cap and two fastening screws, kept inside the physical skirt footprint.
      const cv=document.createElement('canvas');cv.width=256;cv.height=256;const c=cv.getContext('2d');
      c.fillStyle='#e9d6a8';c.fillRect(0,0,256,256);c.strokeStyle='#9b302b';c.lineWidth=12;c.beginPath();c.arc(128,128,112,0,7);c.stroke();
      c.fillStyle='#b92f2d';c.beginPath();c.moveTo(150,42);c.lineTo(75,140);c.lineTo(118,140);c.lineTo(102,213);c.lineTo(187,107);c.lineTo(143,107);c.closePath();c.fill();
      const capTex=new THREE.CanvasTexture(cv);capTex.colorSpace=THREE.SRGBColorSpace;
      const badge=new THREE.Mesh(new THREE.CircleGeometry(radius*.70,40),new THREE.MeshStandardMaterial({map:capTex,roughness:.35}));badge.rotation.x=-Math.PI/2;badge.position.y=.0411;group.add(badge);
      for(const x of [-radius*.57,radius*.57]){const screw=new THREE.Mesh(new THREE.CylinderGeometry(.0017,.0017,.001,12),popLidMat);screw.position.set(x,.042,0);group.add(screw);}
      const ring = new THREE.Mesh(new THREE.TorusGeometry(radius * 0.9, 0.002, 6, 28), popLidMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.022;
      group.add(ring);
      this.pops.push({ grp: group, ring, capMat, pulse: 0, base: 1 });
      this.table.add(group);
    });

    // Drop targets: amber tabs that sink into the playfield when hit (dropAnim in render()).
    this.drops = [];
    const dropMat = new THREE.MeshStandardMaterial({
      color: 0xe8a825,
      roughness: 0.4,
      emissive: 0x5a3800,
      emissiveIntensity: 0.12,
    });
    table.meta.drops.forEach((target) => {
      const midX = (target.a.x + target.b.x) / 2;
      const midY = (target.a.y + target.b.y) / 2;
      const width = Math.hypot(target.b.x - target.a.x, target.b.y - target.a.y) - 0.003;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, 0.024, 0.0075), dropMat);
      mesh.position.copy(toWorld(midX, midY, 0.012));
      mesh.rotation.y = Math.atan2(target.b.y - target.a.y, target.b.x - target.a.x);
      this.drops.push({ mesh, baseY: mesh.position.y });
      this.table.add(mesh);
    });

    // Stand-up targets: fixed red tabs on their collider segments.
    const standMat = new THREE.MeshStandardMaterial({
      color: 0xd42035,
      roughness: 0.4,
      metalness: 0.1,
      emissive: 0x66101c,
      emissiveIntensity: 0.35,
    });
    for (const collider of table.world.colliders) {
      await GameLoading.yieldIfNeeded();
      if (collider.kind !== 'standup') continue;
      const width = Math.hypot(collider.b.x - collider.a.x, collider.b.y - collider.a.y);
      const midX = (collider.a.x + collider.b.x) / 2;
      const midY = (collider.a.y + collider.b.y) / 2;
      const tab = new THREE.Mesh(new THREE.BoxGeometry(width, 0.02, 0.009), standMat);
      tab.position.copy(toWorld(midX, midY, 0.01));
      tab.rotation.y = Math.atan2(collider.b.y - collider.a.y, collider.b.x - collider.a.x);
      this.table.add(tab);
    }

    // Spinner: chrome axle plus a cyan plate hanging from a hinge group that spins in render().
    const spinnerPos = table.meta.spinner ? table.meta.spinner.pos : { x: 0.211, y: 0.803 };
    const spinnerGroup = new THREE.Group();
    spinnerGroup.position.copy(toWorld(spinnerPos.x, spinnerPos.y, 0.02));
    const axle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0012, 0.0012, 0.042, 6),
      new THREE.MeshStandardMaterial({ color: 0xa9b2c4, metalness: 0.9, roughness: 0.3 }),
    );
    axle.rotation.z = Math.PI / 2;
    this.spinnerPlate = new THREE.Mesh(
      new THREE.BoxGeometry(0.036, 0.021, 0.0012),
      new THREE.MeshStandardMaterial({
        color: 0x19e3ff,
        metalness: 0.4,
        roughness: 0.3,
        emissive: 0x0aa7c0,
        emissiveIntensity: 0.5,
        side: THREE.DoubleSide,
      }),
    );
    this.spinnerPlate.position.y = -0.0105;
    const hinge = new THREE.Group();
    hinge.add(this.spinnerPlate);
    spinnerGroup.add(axle, hinge);
    this.spinnerHinge = hinge;
    this.table.add(spinnerGroup);

    // One-way gate: a thin chrome flap on a hinge group that swings when a ball passes (gateSwing in render()).
    const gate = table.world.byTag('gate');
    const gateGroup = new THREE.Group();
    gateGroup.position.copy(toWorld((gate.a.x + gate.b.x) / 2, (gate.a.y + gate.b.y) / 2, 0.024));
    gateGroup.rotation.y = Math.atan2(gate.b.y - gate.a.y, gate.b.x - gate.a.x);
    const gateFlap = new THREE.Mesh(
      new THREE.BoxGeometry(Math.hypot(gate.b.x - gate.a.x, gate.b.y - gate.a.y), 0.0015, 0.02),
      new THREE.MeshStandardMaterial({ color: 0xccd4e4, metalness: 0.9, roughness: 0.25 }),
    );
    gateFlap.position.z = 0.01;
    this.gateHinge = new THREE.Group();
    this.gateHinge.add(gateFlap);
    gateGroup.add(this.gateHinge);
    this.table.add(gateGroup);

    // Only the left (magenta) ramp exists; the right ramp was retired for good.
    if (table.ramps.rampL) this.buildRamp(table.ramps.rampL, 0xff2fa8);
    this.buildMiniCab();

    // Scoop: black hole with a brass rim and a low hood behind it (the held ball is hidden by the rules).
    const scoop = new THREE.Group();
    scoop.position.copy(toWorld(0.304, 0.749, 5e-4));
    const hole = new THREE.Mesh(new THREE.CircleGeometry(0.016, 24), new THREE.MeshBasicMaterial({ color: 0 }));
    hole.rotation.x = -Math.PI / 2;
    const rimMat = new THREE.MeshStandardMaterial({
      color: 0xcf9022,
      metalness: 0.8,
      roughness: 0.35,
      emissive: 0x3a2400,
      emissiveIntensity: 0.18,
    });
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.016, 0.0022, 8, 24), rimMat);
    rim.rotation.x = Math.PI / 2;
    const hood = new THREE.Mesh(
      new THREE.CylinderGeometry(0.02, 0.02, 0.026, 16, 1, false, Math.PI, Math.PI),
      new THREE.MeshStandardMaterial({ color: 0x1a1220, metalness: 0.5, roughness: 0.5, side: THREE.DoubleSide }),
    );
    hood.position.set(0, 0.013, -0.004);
    scoop.add(hole, rim, hood);
    this.table.add(scoop);

    // Outlane kickback assemblies: a guide-mounted axial striker and a visible
    // drain diverter. They are separate from the loop's flipper-return chute.
    this.kickbackUnits={};
    const kickerMetal=new THREE.MeshStandardMaterial({color:0xaebac4,metalness:.9,roughness:.28});
    for(const side of ['L','R']){
      const x=side==='L'?.0195:.431,unit=new THREE.Group();unit.name='kickback'+side;
      const rod=new THREE.Group();rod.position.copy(toWorld(x,.12,.0135));
      const shaft=new THREE.Mesh(new THREE.CylinderGeometry(.0025,.0025,.038,16),kickerMetal);shaft.rotation.x=Math.PI/2;rod.add(shaft);
      const head=new THREE.Mesh(new THREE.CylinderGeometry(.0055,.0055,.003,20),kickerMetal);head.rotation.x=Math.PI/2;head.position.z=-.019;rod.add(head);unit.add(rod);
      const bezel=new THREE.Mesh(new THREE.BoxGeometry(.024,.011,.010),new THREE.MeshStandardMaterial({color:0x25303e,metalness:.7,roughness:.42}));bezel.position.copy(toWorld(x,.116,.006));unit.add(bezel);
      const lamp=new THREE.Mesh(new THREE.SphereGeometry(.0025,12,8),new THREE.MeshBasicMaterial({color:0x21e6ce}));lamp.position.copy(toWorld(x,.109,.012));unit.add(lamp);
      const c=table.meta.kickDoors[side],dx=c.b.x-c.a.x,dy=c.b.y-c.a.y;
      const door=new THREE.Mesh(new THREE.BoxGeometry(Math.hypot(dx,dy),.018,c.r*2),kickerMetal);door.position.copy(toWorld((c.a.x+c.b.x)/2,(c.a.y+c.b.y)/2,-.012));door.rotation.y=Math.atan2(dy,dx);door.name='kickbackDiverter'+side;unit.add(door);
      this.kickbackUnits[side]={rod,restZ:rod.position.z,door,lamp,pulse:0};this.table.add(unit);
    }

    // Plunger: the rod runs from the shooter-lane seat back through a housing on the front panel to a red knob
    // outside the cabinet; the whole assembly slides toward the player with plungerPower in render().
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xb9c2d4, metalness: 0.9, roughness: 0.3 });
    const plunger = new THREE.Group();
    const rodLength = 0.2445 + 0.1; // tip at table y 0.2445 (the plungerRod collider) .. 10 cm outside the front
    const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, rodLength, 10), chromeMat);
    rod.rotation.x = Math.PI / 2;
    rod.position.z = rodLength / 2;
    const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.0065, 0.0065, 0.008, 12), chromeMat);
    tip.rotation.x = Math.PI / 2;
    tip.position.z = 0.004;
    // Turned stainless mushroom knob: rounded flange, recessed face and grip grooves.
    const knobProfile = [[0,0],[.006,0],[.006,.008],[.012,.011],[.016,.015],[.017,.020],[.016,.025],[.013,.028],[0,.028]];
    // The bundled Three build omits LatheGeometry; revolve the profile directly.
    const knobVertices = [], knobIndices = [];
    for (const [radius,y] of knobProfile) for (let j=0;j<=48;j++) {
      const angle=j/48*Math.PI*2;
      knobVertices.push(radius*Math.cos(angle),y,radius*Math.sin(angle));
    }
    for (let i=0;i<knobProfile.length-1;i++) for(let j=0;j<48;j++) {
      const a=i*49+j,b=a+49;
      knobIndices.push(a,b,a+1,a+1,b,b+1);
    }
    const knobGeometry = new THREE.BufferGeometry();
    knobGeometry.setAttribute('position',new THREE.Float32BufferAttribute(knobVertices,3));
    knobGeometry.setIndex(knobIndices);knobGeometry.computeVertexNormals();
    const knob = new THREE.Mesh(knobGeometry,
      new THREE.MeshStandardMaterial({color:0xc8cdd1,metalness:.95,roughness:.23,envMapIntensity:1.7}));
    knob.name = 'metalPlungerKnob';
    knob.rotation.x = Math.PI / 2;
    knob.position.z = rodLength - .021;
    for (const offset of [.017,.020,.023]) {
      const groove = new THREE.Mesh(new THREE.TorusGeometry(.0163,.00045,6,48), chromeMat);
      groove.rotation.x = Math.PI/2;
      groove.position.y = offset;
      knob.add(groove);
    }
    plunger.add(rod, tip, knob);
    plunger.name = 'plungerRod';
    plunger.position.copy(toWorld(0.48, table.meta.plunger.seat.y - TABLE.BALL_R, TABLE.BALL_R));
    this.plungerRod = plunger;
    this.plungerRestZ = plunger.position.z;
    this.table.add(plunger);
    // Housing: a barrel through the front panel with a mounting plate on the apron (does not move).
    const housing = new THREE.Group();
    housing.name = 'plungerHousing';
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 0.075, 14), chromeMat);
    barrel.rotation.x = Math.PI / 2;
    const plate = new THREE.Mesh(
      new THREE.BoxGeometry(0.036, 0.036, 0.006),
      new THREE.MeshStandardMaterial({ color: 0x2a2a34, metalness: 0.7, roughness: 0.4 }),
    );
    plate.position.z = 0.02;
    // Helical compression spring: fixed rear collar, moving front end on the rod.
    const coilPoints = [];
    for (let i = 0; i <= 240; i++) {
      const t = i / 240, angle = t * Math.PI * 2 * 12;
      coilPoints.push(new THREE.Vector3(Math.cos(angle) * 0.007, Math.sin(angle) * 0.007, t * 0.13));
    }
    const spring = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(coilPoints), 240, 0.001, 6, false), chromeMat);
    spring.name = 'plungerSpring';
    this.plungerSpring = spring;
    spring.position.z = -0.145;
    this.springRestZ = spring.position.z;
    housing.add(barrel, plate, spring);
    housing.position.set(0.48 - TABLE.W / 2, 0.013, CAB_FRONT_Z);
    this.table.add(housing);
    const cradle = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.0015, 0.040), chromeMat);
    cradle.name = 'shooterCradle';
    cradle.position.copy(toWorld(0.48, table.meta.plunger.seat.y, 0.00075));
    this.table.add(cradle);
    const apron = new THREE.Mesh(new THREE.BoxGeometry(0.038, 0.002, 0.205),
      new THREE.MeshStandardMaterial({ color: 0x20232d, metalness: 0.55, roughness: 0.4 }));
    apron.name = 'shooterApron';
    apron.position.copy(toWorld(0.48, 0.077, 0.024));
    this.table.add(apron);


    // Glass: one clearcoat sheet over the playfield (no map, no transmission) drawn after everything else.
    const glassGeo = new THREE.PlaneGeometry(TABLE.W + 0.02, TABLE.L + 0.06);
    glassGeo.rotateX(-Math.PI / 2);
    this.glass = new THREE.Mesh(
      glassGeo,
      new THREE.MeshPhysicalMaterial({
        color: 0xbfd6ff,
        transparent: true,
        opacity: 0.06,
        roughness: 0.06,
        metalness: 0,
        clearcoat: 1,
        clearcoatRoughness: 0.08,
        envMapIntensity: 0.35,
        specularIntensity: 0.35,
        depthWrite: false,
      }),
    );
    this.glass.name = 'glass';
    this.glass.position.set(0, GLASS_Z, 0);
    this.glass.renderOrder = 20;
    this.table.add(this.glass);

    // Soft dark blob used as the ball's contact shadow; ball geometry/materials are shared by every ball.
    this.blobTex = new THREE.CanvasTexture(makeBlobTexture(64, 'rgba(0,0,0,0.85)'));
    this.ballGeo = new THREE.SphereGeometry(TABLE.BALL_R, 26, 20);
    this.ballMat = new THREE.MeshStandardMaterial({
      color: 0xf2f4fa,
      metalness: 1,
      roughness: 0.07,
      envMapIntensity: 1.9,
      emissive: 0x14161c,
      emissiveIntensity: 1,
    });
    this.blobGeo = new THREE.PlaneGeometry(0.034, 0.034);
    this.blobGeo.rotateX(-Math.PI / 2);
  }

  /** Die-cut, reverse-printed 2.4 mm plastics follow occupied guide islands only. */
  buildPlayfieldPlastics() {
    const specs = [
      {name:'leftSideApron',side:'L',type:'apron',solid:true,points:[[0,.3244],[.0007,.3244],[.0331,.4928],[.0526,.6756],[.0402,.742],[.0179,.8565],[.0112,.8967],[0,.8967]],bolts:[[.015,.54],[.025,.66],[.02,.75]]},
      { name:'slingPlasticL', side:'L', type:'sling', points:[...WALL_POLYLINES[12], [.126,.252], [.101,.321], [.0947,.3343], [.0907,.3416], [.0876,.3435]], bolts:[[.083,.323],[.083,.258],[.119,.234]] },
      { name:'slingPlasticR', side:'R', type:'sling', points:[...WALL_POLYLINES[15], [.3755,.3366],[.3697,.344],[.3625,.3416],[.354,.322],[.329,.25],[.3236,.2342],[.3236,.2279]], bolts:[[.370,.323],[.370,.258],[.334,.234]] },
      { name:'returnPlasticL', side:'L', type:'return', points:WALL_POLYLINES[3], bolts:[[.044,.211],[.122,.163]] },
      { name:'returnPlasticR', side:'R', type:'return', points:WALL_POLYLINES[1], bolts:[[.404,.211],[.328,.163]] },
    ];
    this.playfieldPlastics = new THREE.Group();
    this.playfieldPlastics.name = 'playfieldPlastics';
    this.table.add(this.playfieldPlastics);
    this.slings = {};
    const metal = new THREE.MeshStandardMaterial({color:0xa7b2ba,metalness:.88,roughness:.3});
    const slotMat = new THREE.MeshStandardMaterial({color:0x232c33,roughness:.7});
    for (const spec of specs) {
      const group = new THREE.Group(); group.name = spec.name;
      const shape = new THREE.Shape();
      spec.points.forEach(([x,y],i)=>shape[i?'lineTo':'moveTo'](x-TABLE.W/2,y-TABLE.L/2));
      shape.closePath();
      const base = new THREE.ExtrudeGeometry(shape,{depth:spec.solid?.0244:.0024,bevelEnabled:false,curveSegments:3});
      base.rotateX(-Math.PI/2); base.translate(0,spec.solid?0:.022,0);
      const edge = new THREE.Mesh(base,new THREE.MeshPhysicalMaterial({color:0xdbdfe8,metalness:0,roughness:.28,clearcoat:.85,clearcoatRoughness:.18}));
      edge.name = spec.name+'Edge'; group.add(edge);
      const {canvas,bounds} = paintPlayfieldPlastic(spec);
      const tex = new THREE.CanvasTexture(canvas); tex.colorSpace=THREE.SRGBColorSpace; tex.anisotropy=Math.min(8,this.maxAnisotropy);
      // This bundled Three build supplies extrusion triangulation, but no ShapeGeometry.
      // Reuse just its upward cap for the printed face (no duplicate coplanar back face).
      const cap=new THREE.ExtrudeGeometry(shape,{depth:.001,bevelEnabled:false});
      const cp=cap.attributes.position,cn=cap.attributes.normal,verts=[],uvs=[];
      for(let i=0;i<cp.count;i++)if(cn.getZ(i)>.9){
        const x=cp.getX(i),y=cp.getY(i);verts.push(x,y,0);
        uvs.push((x+TABLE.W/2-bounds.x0)/bounds.w,(y+TABLE.L/2-bounds.y0)/bounds.h);
      }
      const printGeo=new THREE.BufferGeometry();
      printGeo.setAttribute('position',new THREE.Float32BufferAttribute(verts,3));
      printGeo.setAttribute('uv',new THREE.Float32BufferAttribute(uvs,2));
      printGeo.computeVertexNormals();cap.dispose();
      printGeo.rotateX(-Math.PI/2); printGeo.translate(0,.02445,0);
      const print = new THREE.Mesh(printGeo,new THREE.MeshPhysicalMaterial({map:tex,emissiveMap:tex,emissive:0xffffff,emissiveIntensity:.2,roughness:.34,clearcoat:.65,clearcoatRoughness:.25}));
      print.name=spec.name+'Print'; group.add(print);
      for(const [x,y] of spec.bolts){
        const post=new THREE.Mesh(new THREE.CylinderGeometry(.0017,.0017,.022,12),metal);post.position.copy(toWorld(x,y,.011));group.add(post);
        const washer=new THREE.Mesh(new THREE.CylinderGeometry(.0031,.0031,.00065,20),metal);washer.position.copy(toWorld(x,y,.0249));group.add(washer);
        const screw=new THREE.Mesh(new THREE.CylinderGeometry(.0019,.0021,.0011,16),metal);screw.position.copy(toWorld(x,y,.0257));group.add(screw);
        for(const angle of [0,Math.PI/2]){const slot=new THREE.Mesh(new THREE.BoxGeometry(.0026,.00012,.00042),slotMat);slot.position.copy(toWorld(x,y,.0263));slot.rotation.y=angle;group.add(slot);}
      }
      if(spec.type==='sling'){
        const sling=this.T.world.byTag('sling'+spec.side);
        const rubber=new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([toWorld(sling.a.x,sling.a.y,.0135),toWorld(sling.b.x,sling.b.y,.0135)]),1,sling.r,12),new THREE.MeshStandardMaterial({color:0xeee5cb,roughness:.65}));
        rubber.name='slingRubber'+spec.side;group.add(rubber);
        this.slings[spec.side]={grp:rubber,pulse:0};
      }
      this.playfieldPlastics.add(group);
    }
  }

  /**
   * Rails from the colliders that are active on the ball layer: every wall segment becomes a box (arcs a fan of
   * boxes) plus round posts, bucketed into four merged meshes: dark rails, steel guides, red rubber posts and the
   * thin transparent inlane wires. Devices (pops, drops, standups, slings, gate, kick doors, the plunger floor)
   * and colliders outside the playfield (the catch-all frame top) are drawn by their own builders or not at all.
   */
  buildRails() {
    const railHeight = 0.021;
    const guideHeight = 0.017;
    const railParts = [];
    const guideParts = [];
    const postParts = [];
    const wireParts = [];
    const guideTags = new Set([
      'guideL',
      'guideL2',
      'guideR',
      'guideR2',
      'orbitGuideL',
      'orbitGuideR',
      'laneDiv',
      'popGuide',
      'orbitFlareL',
    ]);
    // Kinds table.js gives its smooth replacement faces and the short pocket-opening rails
    // ('right-guide-smooth-wall', 'physical-pocket-path', 'bumper-path-wall', 'chamfer'; 'fillet' is reserved
    // for the same job). They all read as steel guides rather than dark rails.
    const guideKinds = /guide|pocket-path|path-wall|chamfer|fillet/;
    const inside = (p) => p.x > -0.01 && p.x < TABLE.W + 0.01 && p.y > -0.01 && p.y < TABLE.L + 0.02;
    for (const collider of this.T.world.colliders) {
      if (collider.active === false) continue;
      if (collider.kind === 'drop' || collider.kind === 'pop' || collider.kind === 'standup') continue;
      const tag = collider.tag || '';
      if (tag === 'gate' || tag === 'plungerRod' || tag.startsWith('sling') || tag.startsWith('kickDoor')) continue;
      if (tag.endsWith('Wall') || !(collider.layers & 1)) continue;
      if (collider.type === 'seg' && !inside(collider.a) && !inside(collider.b)) continue;
      // Untagged hair-thin segments around y≈0.62 are the inlane wire-forms: drawn as raised thin glass-like bars.
      const isLaneWire =
        !collider.tag && collider.type === 'seg' && collider.sourcePolyline == null &&
        collider.a.y > 0.6 && collider.a.y < 0.65 && collider.b.y > 0.6 && collider.b.y < 0.65 && collider.r < 0.004;
      const isGuide = guideTags.has(collider.tag) || guideKinds.test(collider.kind || '');
      if (collider.type === 'seg') {
        const length = Math.hypot(collider.b.x - collider.a.x, collider.b.y - collider.a.y);
        const midX = (collider.a.x + collider.b.x) / 2;
        const midY = (collider.a.y + collider.b.y) / 2;
        const angle = Math.atan2(collider.b.y - collider.a.y, collider.b.x - collider.a.x);
        const height = isLaneWire ? 0.003 : isGuide ? guideHeight : railHeight;
        const bar = new THREE.BoxGeometry(length, height, Math.max(0.0016, collider.r * 2));
        const barMatrix = new THREE.Matrix4()
          .makeRotationY(angle)
          .setPosition(toWorld(midX, midY, isLaneWire ? 0.03 : height / 2));
        const post = new THREE.CylinderGeometry(Math.max(0.0008, collider.r), Math.max(0.0008, collider.r), height, 10);
        const postLift = isLaneWire ? 0.03 : height / 2;
        const postA = new THREE.Matrix4().setPosition(toWorld(collider.a.x, collider.a.y, postLift));
        const postB = new THREE.Matrix4().setPosition(toWorld(collider.b.x, collider.b.y, postLift));
        const bucket = isLaneWire ? wireParts : isGuide ? guideParts : railParts;
        bucket.push({ geo: bar, m: barMatrix }, { geo: post.clone(), m: postA }, { geo: post, m: postB });
      } else if (collider.type === 'arc') {
        let sweep = (collider.a1 - collider.a0) % (Math.PI * 2);
        if (sweep < 0) sweep += Math.PI * 2;
        const steps = Math.max(10, Math.round(sweep * collider.R * 240));
        const ring = [];
        for (let i = 0; i <= steps; i++) {
          const theta = collider.a0 + (sweep * i) / steps;
          ring.push([collider.c.x + Math.cos(theta) * collider.R, collider.c.y + Math.sin(theta) * collider.R]);
        }
        for (let i = 0; i < steps; i++) {
          const [x0, y0] = ring[i];
          const [x1, y1] = ring[i + 1];
          const chord = Math.hypot(x1 - x0, y1 - y0);
          // 6% overlength hides the seams between consecutive chord boxes.
          const piece = new THREE.BoxGeometry(chord * 1.06, railHeight, Math.max(0.0016, collider.r * 2));
          const pieceMatrix = new THREE.Matrix4()
            .makeRotationY(Math.atan2(y1 - y0, x1 - x0))
            .setPosition(toWorld((x0 + x1) / 2, (y0 + y1) / 2, railHeight / 2));
          railParts.push({ geo: piece, m: pieceMatrix });
        }
      } else if (collider.type === 'circle') {
        const post = new THREE.CylinderGeometry(collider.r + 0.0012, collider.r + 0.0022, 0.022, 14);
        postParts.push({ geo: post, m: new THREE.Matrix4().setPosition(toWorld(collider.c.x, collider.c.y, 0.011)) });
      }
    }
    const railMat = new THREE.MeshStandardMaterial({
      color: 0x656e75,
      metalness: 0.55,
      roughness: 0.5,
      envMapIntensity: 0.5,
    });
    const guideMat = new THREE.MeshStandardMaterial({
      color: 0xb9c2d4,
      metalness: 0.95,
      roughness: 0.3,
      envMapIntensity: 0.8,
    });
    const postMat = new THREE.MeshStandardMaterial({ color: 0xd42035, metalness: 0.1, roughness: 0.55 });
    const wireMat = new THREE.MeshPhysicalMaterial({
      color: 0x8fb6ff,
      metalness: 0,
      roughness: 0.12,
      transparent: true,
      opacity: 0.55,
      envMapIntensity: 1.4,
    });
    for (const [parts, material, name] of [
      [railParts, railMat, 'rails'],
      [guideParts, guideMat, 'guides'],
      [postParts, postMat, 'posts'],
      [wireParts, wireMat, 'laneWires'],
    ]) {
      if (!parts.length) continue;
      const mesh = new THREE.Mesh(mergeGeometries(parts), material);
      mesh.name = name;
      this.table.add(mesh);
    }
  }

  /**
   * Ramp wireform: a translucent deck swept along the path with two neon-lit wire rails on its lips and chrome
   * support posts down to the playfield. The cross-section is an OPEN channel (floor plus two short upstands),
   * not a closed tube: a camera ray then crosses one or two translucent faces instead of four, so the deck reads
   * as a deck instead of compositing into an opaque slab that hides the playfield behind it. The last frames
   * narrow into the right inlane and sink their walls to nothing, where a short steel
   * apron carries the ball off. The path (table.js) descends to z = 0 at its end.
   */
  buildRamp(ramp, neonColor) {
    const steps=ramp.frames.length-1;
    const floorLift=.0015,wallHeight=.013,flareFrames=12;
    const positions=[],normals=[],indices=[];
    const frames=ramp.frames.map((f,i)=>{
      const p=toWorld(f.p[0],f.p[1],f.p[2]-floorLift);
      const side=new THREE.Vector3(f.n[0],0,-f.n[1]);
      const tangent=new THREE.Vector3(f.t[0],0,-f.t[1]);
      const exit=clamp((i-(steps-flareFrames))/flareFrames,0,1);
      const half=f.width/2,deckHalf=half-.0017*clamp((.350-f.p[1])/.030,0,1);
      return {p,side,tangent,half,deckHalf,wall:wallHeight*(1-exit)};
    });
    // Cross-section (across, lift), four points per ring: left upstand, floor, right upstand.
    for (const frame of frames) {
      const { p, side } = frame;
      const profile = [
        [-frame.deckHalf, floorLift + frame.wall],
        [-frame.deckHalf, floorLift],
        [frame.deckHalf, floorLift],
        [frame.deckHalf, floorLift + frame.wall],
      ];
      for (const [across, lift] of profile) {
        positions.push(p.x + side.x * across, p.y + lift, p.z + side.z * across);
        normals.push(0, 1, 0);
      }
    }
    const ringSize = 4;
    for (let i = 0; i < steps; i++) {
      for (let j = 0; j < ringSize - 1; j++) {
        const a = i * ringSize + j;
        const b = a + ringSize;
        indices.push(a, b, a + 1, a + 1, b, b + 1);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    // The return is folded stainless steel. Its opaque floor makes the separation
    // from the guides underneath legible; the upper loop remains smoked acrylic.
    const deckMaterialAt=i=>i<ramp.entryBaseEnd||TABLE.L/2-frames[i].p.z<.60?1:0;
    let groupStart=0,groupMaterial=deckMaterialAt(0);
    for(let i=1;i<=steps;i++){
      const material=i===steps?-1:deckMaterialAt(i);
      if(material!==groupMaterial){geometry.addGroup(groupStart*18,(i-groupStart)*18,groupMaterial);groupStart=i;groupMaterial=material;}
    }
    const deck = new THREE.Mesh(
      geometry,
      new THREE.MeshPhysicalMaterial({
        // Smoked steel-grey with only a hint of the lane's neon: the rails carry the colour, the deck stays glass.
        // Kept near the playfield's own brightness on purpose — a deck that outshines the art under it reads as a
        // solid slab however low its alpha is.
        color: new THREE.Color(0x7f8695).lerp(new THREE.Color(neonColor), 0.2),
        emissive: new THREE.Color(neonColor).multiplyScalar(0.05),
        transparent: true,
        opacity: 0.30,
        roughness: 0.34,
        metalness: 0,
        envMapIntensity: 0.45,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    deck.material=[deck.material,new THREE.MeshStandardMaterial({color:0x8d9eae,metalness:.78,roughness:.4,envMapIntensity:.7,side:THREE.DoubleSide})];
    deck.name = 'rampDeck';
    deck.renderOrder = 5;
    this.table.add(deck);
    // Ground-to-deck entrance housing, matching the low ramp's collision base.
    // The uphill closure stops a ball entering underneath until there is headroom.
    const baseVertices=[];
    const basePoint=(f,side,z)=>toWorld(f.p[0]+f.n[0]*f.width/2*side,f.p[1]+f.n[1]*f.width/2*side,z);
    const baseQuad=(a,b,c,d)=>{for(const p of [a,b,c,a,c,d])baseVertices.push(p.x,p.y,p.z);};
    for(const side of [-1,1])for(let i=0;i<steps;i++){
      if(i>=ramp.entryBaseEnd&&i<ramp.returnBaseStart)continue;
      const a=ramp.frames[i],b=ramp.frames[i+1];
      baseQuad(basePoint(a,side,0),basePoint(b,side,0),basePoint(b,side,b.p[2]),basePoint(a,side,a.p[2]));
    }
    for(const index of [ramp.entryBaseEnd,ramp.returnBaseStart]){
      const end=ramp.frames[index];
      baseQuad(basePoint(end,-1,0),basePoint(end,1,0),basePoint(end,1,end.p[2]),basePoint(end,-1,end.p[2]));
    }
    // A folded wing closes the narrow space against the orbit guide and sheds
    // descending ground balls into the open spinner lane.
    for(const p of ramp.baseWing){const v=toWorld(p[0],p[1],.017);baseVertices.push(v.x,v.y,v.z);}
    for(let i=0;i<3;i++){
      const a=ramp.returnWing[i],b=ramp.returnWing[(i+1)%3];
      baseQuad(toWorld(...a,0),toWorld(...b,0),toWorld(...b,.017),toWorld(...a,.017));
    }
    for(const a of ramp.returnWing){const v=toWorld(...a,.017);baseVertices.push(v.x,v.y,v.z);}
    const baseGeo=new THREE.BufferGeometry();baseGeo.setAttribute('position',new THREE.Float32BufferAttribute(baseVertices,3));baseGeo.computeVertexNormals();
    const baseMesh=new THREE.Mesh(baseGeo,deck.material[1]);baseMesh.name='rampLowHousings';this.table.add(baseMesh);
    const gate=ramp.returnGate,hinge=new THREE.Group();
    hinge.position.copy(toWorld((gate.a.x+gate.b.x)/2,(gate.a.y+gate.b.y)/2,.028));
    const blade=new THREE.Mesh(new THREE.BoxGeometry(ramp.frames[steps].width,.026,.0016),deck.material[1]);
    blade.position.y=-.013;hinge.add(blade);hinge.name='returnCheckFlap';
    const axle=new THREE.Mesh(new THREE.CylinderGeometry(.0015,.0015,ramp.frames[steps].width+.005,12),deck.material[1]);
    axle.rotation.z=Math.PI/2;hinge.add(axle);
    this.returnFlap=hinge;this.table.add(hinge);
    // Three-millimetre steel wire, matching the physical return-rail diameter.
    const wireMat = new THREE.MeshStandardMaterial({
      color: 0xe6ebf4,
      emissive: new THREE.Color(neonColor).multiplyScalar(0.3),
      metalness: 0.9,
      roughness: 0.2,
      envMapIntensity: 1.2,
    });
    for (const dir of [-1, 1]) {
      const railPoints = frames.filter(f=>TABLE.L/2-f.p.z>=.307).map(
        ({ p, side, half: h, wall }) =>
          new THREE.Vector3(p.x + side.x * dir * h, p.y + floorLift + wall + 0.0016, p.z + side.z * dir * h),
      );
      // Formed wires land on the tops of the inlane guides, which take over as
      // sidewalls. Do not drive the return wires through those steel sheets.
      railPoints.push(toWorld(dir<0?.379:.412,.287,.0225));
      const railCurve = new THREE.CatmullRomCurve3(railPoints,false,'catmullrom',.12);
      const rail = new THREE.Mesh(new THREE.TubeGeometry(railCurve, steps, 0.0015, 10), wireMat);
      rail.name = 'rampRail';
      this.table.add(rail);
    }
    // Support posts wherever the deck stands clear of the playfield: sparse and dark, so they
    // read as the few stanchions a real wireform runs on instead of a picket fence.
    const postMat = new THREE.MeshStandardMaterial({
      color: 0x5d646f,
      metalness: 0.85,
      roughness: 0.42,
      envMapIntensity: 0.5,
    });
    const postParts = [];
    for (let i = 8; i < steps - flareFrames; i += Math.round(steps/6)) {
      const { p, side, half: h } = frames[i];
      const height = p.y + floorLift;
      if (height < 0.016) continue;
      for (const dir of [-1, 1]) {
        const post = new THREE.CylinderGeometry(9e-4, 9e-4, height, 6);
        const inset = h - 0.002;
        const m = new THREE.Matrix4().setPosition(p.x + side.x * dir * inset, height / 2, p.z + side.z * dir * inset);
        postParts.push({ geo: post, m });
      }
    }
    if (postParts.length) {
      const posts = new THREE.Mesh(mergeGeometries(postParts), postMat);
      posts.name = 'rampPosts';
      this.table.add(posts);
    }
    // Saddles fixed to existing guide ends support the bridge without adding posts
    // inside a playable ball lane. Their underside stays above a rolling ball.
    const returnFrameAt=(y)=>{
      for(let i=0;i<frames.length-1;i++){
        const a=frames[i],b=frames[i+1],ay=TABLE.L/2-a.p.z,by=TABLE.L/2-b.p.z;
        if(a.p.x>.12 && ay>=y && by<=y){const t=(ay-y)/(ay-by);return {x:TABLE.W/2+a.p.x+(b.p.x-a.p.x)*t,floor:a.p.y+(b.p.y-a.p.y)*t+floorLift,half:a.deckHalf+(b.deckHalf-a.deckHalf)*t};}
      }
      throw new Error('Return saddle is outside its deck');
    };
    for(const [x,y] of [[.4238,.473]]){
      const f=returnFrameAt(y);
      const h=Math.min(returnFrameAt(y-.0025).floor,returnFrameAt(y+.0025).floor)-.001;
      const saddle=new THREE.Group();saddle.name='rampReturnSaddle';
      const beam=new THREE.Mesh(new THREE.BoxGeometry(f.half*2,.0015,.005),postMat);beam.position.copy(toWorld(f.x,y,h));saddle.add(beam);
      const stem=new THREE.Mesh(new THREE.CylinderGeometry(.0012,.0012,h-.021,12),postMat);stem.position.copy(toWorld(x,y,(h+.021)/2));saddle.add(stem);
      this.table.add(saddle);
    }

    // Exit apron: the flat steel flap the ball rolls off, so the deck ends on the playfield instead of in mid-air.
    const last = frames[frames.length - 1];
    const lip = last.tangent.clone();
    lip.y = 0;
    if (lip.lengthSq() > 1e-9) {
      lip.normalize().multiplyScalar(0.024);
      const tip = last.p.clone().add(lip);
      const inner = last.deckHalf;
      const outer = last.deckHalf;
      const corner = [];
      const push = (base, across, height) =>
        corner.push(base.x + last.side.x * across, height, base.z + last.side.z * across);
      push(last.p, -inner, last.p.y + floorLift);
      push(last.p, inner, last.p.y + floorLift);
      push(tip, -outer, 0.0009);
      push(tip, outer, 0.0009);
      const apronGeo = new THREE.BufferGeometry();
      apronGeo.setAttribute('position', new THREE.Float32BufferAttribute(corner, 3));
      apronGeo.setIndex([0, 2, 1, 1, 2, 3]);
      apronGeo.computeVertexNormals();
      const apron = new THREE.Mesh(
        apronGeo,
        new THREE.MeshStandardMaterial({
          color: 0xc9cfda,
          metalness: 0.35,
          roughness: 0.4,
          envMapIntensity: 0.8,
          side: THREE.DoubleSide,
        }),
      );
      apron.name = 'rampApron';
      this.table.add(apron);
    }
  }

  /**
   * Miniature X-Arcade cabinet toy on the playfield at 65% of its footprint (side art, animated mini screen,
   * marquee, panel, joystick, two buttons), squat enough to stay under the glass.
   */
  buildMiniCab() {
    const cab = this.T.meta.cabinet;
    const scale = 0.65;
    const group = new THREE.Group();
    group.name = 'miniCab';
    const width = (cab.x1 - cab.x0) * scale;
    const depth = (cab.y1 - cab.y0) * scale;
    group.position.copy(toWorld((cab.x0 + cab.x1) / 2, (cab.y0 + cab.y1) / 2, 0));
    const height = 0.049; // body top leaves room for the marquee under the glass
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(width, height, depth),
      new THREE.MeshStandardMaterial({ color: 0x141218, metalness: 0.35, roughness: 0.5 }),
    );
    body.position.y = height / 2;
    const sideArtTex = new THREE.CanvasTexture(paintSideArt(256, 384));
    sideArtTex.colorSpace = THREE.SRGBColorSpace;
    sideArtTex.anisotropy = this.maxAnisotropy;
    const sideArtMat = new THREE.MeshBasicMaterial({ map: sideArtTex });
    const leftArt = new THREE.Mesh(new THREE.PlaneGeometry(depth, height), sideArtMat);
    leftArt.rotation.y = -Math.PI / 2;
    leftArt.position.set(-width / 2 - 6e-4, height / 2, 0);
    // The same texture reads correctly on both faces (a plane's u axis follows its local +x).
    const rightArt = new THREE.Mesh(leftArt.geometry, sideArtMat);
    rightArt.rotation.y = Math.PI / 2;
    rightArt.position.set(width / 2 + 6e-4, height / 2, 0);
    // Mini screen: a 96x64 canvas animation redrawn ~11 Hz in render(); nearest filtering keeps the pixels crisp.
    this.miniScreen = new MiniScreen();
    this.miniScreenTex = new THREE.CanvasTexture(this.miniScreen.cv);
    this.miniScreenTex.colorSpace = THREE.SRGBColorSpace;
    this.miniScreenTex.minFilter = THREE.NearestFilter;
    this.miniScreenTex.magFilter = THREE.NearestFilter;
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(width * 0.74, height * 0.4),
      new THREE.MeshBasicMaterial({ map: this.miniScreenTex, color: new THREE.Color(1.6, 1.6, 1.6) }),
    );
    screen.position.set(0, height * 0.62, depth / 2 + 8e-4);
    screen.rotation.x = -0.06;
    // Marquee: "X-ARCADE" text with a red glow; its brightness pulses during multiball (marqueeMat in render()).
    const marqueeCanvas = document.createElement('canvas');
    marqueeCanvas.width = 256;
    marqueeCanvas.height = 64;
    const marqueeCtx = marqueeCanvas.getContext('2d');
    marqueeCtx.fillStyle = '#12040a';
    marqueeCtx.fillRect(0, 0, 256, 64);
    marqueeCtx.font = '900 30px Arial';
    marqueeCtx.textAlign = 'center';
    marqueeCtx.textBaseline = 'middle';
    marqueeCtx.shadowColor = '#ff2033';
    marqueeCtx.shadowBlur = 16;
    marqueeCtx.fillStyle = '#ffffff';
    marqueeCtx.fillText('X-ARCADE', 128, 34);
    const marqueeTex = new THREE.CanvasTexture(marqueeCanvas);
    marqueeTex.colorSpace = THREE.SRGBColorSpace;
    this.marqueeMat = new THREE.MeshBasicMaterial({ map: marqueeTex, color: new THREE.Color(1.35, 1.35, 1.35) });
    const marqueeH = 0.014;
    const marquee = new THREE.Mesh(new THREE.BoxGeometry(width * 1.04, marqueeH, 0.005), this.marqueeMat);
    marquee.position.set(0, height + marqueeH / 2, depth / 2 - 0.005);
    const controlPanel = new THREE.Mesh(
      new THREE.BoxGeometry(width * 0.9, 0.004, 0.011),
      new THREE.MeshStandardMaterial({ color: 0x1c1a22, roughness: 0.5 }),
    );
    controlPanel.position.set(0, height * 0.4, depth / 2 + 0.003);
    controlPanel.rotation.x = 0.5;
    const joystick = new THREE.Mesh(
      new THREE.SphereGeometry(0.0024, 10, 10),
      new THREE.MeshStandardMaterial({ color: 0xd42035, roughness: 0.3 }),
    );
    joystick.position.set(-width * 0.22, height * 0.44, depth / 2 + 0.006);
    for (let i = 0; i < 2; i++) {
      const button = new THREE.Mesh(
        new THREE.CylinderGeometry(0.0019, 0.0019, 0.002, 10),
        new THREE.MeshStandardMaterial({
          color: i ? 0x19e3ff : 0xffb020,
          roughness: 0.3,
          emissive: i ? 0x0a5866 : 0x664400,
          emissiveIntensity: 0.6,
        }),
      );
      button.position.set(width * (0.1 + i * 0.16), height * 0.43, depth / 2 + 0.006);
      button.rotation.x = 0.5;
      group.add(button);
    }
    group.add(body, leftArt, rightArt, screen, marquee, controlPanel, joystick);
    this.miniCabGrp = group;
    this.table.add(group);
    // Reusable arcade-energy burst, separate from the rigid toy and ball physics.
    const celebration=new THREE.Group();celebration.name='arcadeAchievement';
    celebration.position.copy(group.position);celebration.position.y=.056;
    celebration.visible=false;this.table.add(celebration);
    const colors=[0x22e6da,0xffcb65,0xff4563],shards=[];
    const shardGeo=new THREE.BoxGeometry(.003,.003,.001);
    for(let i=0;i<36;i++){
      const material=new THREE.MeshBasicMaterial({color:colors[i%3],transparent:true,opacity:0,depthWrite:false});
      const mesh=new THREE.Mesh(shardGeo,material);celebration.add(mesh);
      shards.push({mesh,angle:i*2.399963,speed:.045+(i%7)*.009,lift:.02+(i%5)*.012});
    }
    const rings=[];
    for(let i=0;i<2;i++){
      const ring=new THREE.Mesh(new THREE.TorusGeometry(.018,.0008,6,64),
        new THREE.MeshBasicMaterial({color:colors[i],transparent:true,opacity:0,depthWrite:false}));
      ring.rotation.x=i?Math.PI/2:.15;celebration.add(ring);rings.push(ring);
    }
    const glow=new THREE.PointLight(0x27e6cf,0,.23,2);glow.position.y=.016;celebration.add(glow);
    this.arcadeCelebration={group:celebration,shards,rings,glow,started:-10,duration:1.5,strength:1,label:''};
  }

  /**
   * The machine around the playfield, all under machineRoot: cabinet body (sides with art, bottom, front and
   * rear panels, lockbar, coin door, neon side strips) pitched with the table; legs standing on the floor at
   * the cabinet corners; the backbox (translite, DMD in its bezel) seated upright on a neck at the cabinet rear.
   */
  /** Commercial-style external controls and two-chute coin door, scaled to this cabinet. */
  buildCabinetHardware(cabinet) {
    const steel=new THREE.MeshStandardMaterial({color:0x252a2c,metalness:.72,roughness:.53});
    const black=new THREE.MeshStandardMaterial({color:0x080b0c,roughness:.82});
    const chrome=new THREE.MeshStandardMaterial({color:0xadb7bb,metalness:.92,roughness:.26});
    const faceZ=CAB_FRONT_Z+.016;
    const box=(parent,name,w,h,d,x,y,z,mat)=>{const rounded=/coinDoor|coinEntry|coinReject/.test(name);const m=new THREE.Mesh(rounded?roundedPanelGeometry(w,h,d,name.startsWith('coinDoor')?.006:.002):new THREE.BoxGeometry(w,h,d),mat);m.name=name;m.position.set(x,y,z);parent.add(m);return m;};
    const label=(parent,text,w,h,x,y,z,ink='#dcd5bd',paper='#182022')=>{
      const cv=document.createElement('canvas');cv.width=512;cv.height=128;const c=cv.getContext('2d');
      if(paper){c.fillStyle=paper;c.fillRect(0,0,512,128);}c.fillStyle=ink;c.textAlign='center';c.textBaseline='middle';c.font='bold 48px Arial';c.fillText(text,256,66,480);
      const tex=new THREE.CanvasTexture(cv);tex.colorSpace=THREE.SRGBColorSpace;
      const m=new THREE.Mesh(new THREE.PlaneGeometry(w,h),new THREE.MeshBasicMaterial({map:tex,transparent:!paper,depthWrite:!!paper}));m.position.set(x,y,z);parent.add(m);return m;
    };
    const screw=(parent,x,y,z)=>{
      const m=new THREE.Mesh(new THREE.CylinderGeometry(.0027,.0027,.0015,12),chrome);m.rotation.x=Math.PI/2;m.position.set(x,y,z);parent.add(m);
      box(parent,'screwSlot',.0037,.00055,.0003,x,y,z+.001,black);
    };
    const door=new THREE.Group();door.name='coinDoorAssembly';door.position.set(0,-.115,faceZ);
    // Commercial 13.5 x 11.69 inch outer frame; cabinet depth accommodates its height.
    door.scale.set(.3429/.286,.296926/.236,1);
    cabinet.add(door);
    box(door,'coinDoorGasket',.286,.236,.003,0,0,.001,black);
    box(door,'coinDoorFlange',.278,.229,.005,0,0,.004,steel);
    box(door,'coinDoorSeam',.256,.209,.003,0,0,.007,black);
    box(door,'coinDoorPanel',.248,.201,.006,0,0,.009,steel);
    for(const x of [-.131,.131])for(const y of [-.105,.105])screw(door,x,y,.0075);
    for(const y of [-.068,0,.068]) {
      const hinge=new THREE.Mesh(new THREE.CylinderGeometry(.003,.003,.025,10),steel);hinge.name='coinDoorHinge';hinge.position.set(-.125,y,.013);door.add(hinge);
    }
    const lock=new THREE.Mesh(new THREE.CylinderGeometry(.009,.009,.004,24),chrome);lock.name='coinDoorLock';lock.rotation.x=Math.PI/2;lock.position.set(.103,.01,.015);door.add(lock);
    box(door,'lockKeyway',.0015,.009,.0006,.103,.01,.0175,black);
    for(const x of [-.051,.038]) {
      box(door,'coinEntryBezel',.063,.066,.009,x,.044,.016,black);
      box(door,'coinEntryTrim',.055,.058,.003,x,.044,.022,chrome);
      box(door,'coinEntryFace',.051,.054,.003,x,.044,.024,steel);
      box(door,'coinSlot',.029,.004,.001,x,.059,.026,black);
      box(door,'coinRejectButton',.041,.022,.004,x,.033,.028,new THREE.MeshStandardMaterial({color:0xf09735,emissive:0xd85a16,emissiveIntensity:.35,roughness:.33}));
      label(door,'PUSH TO REJECT',.037,.008,x,.034,.0305,'#342113',null);
      // A deep black return pocket surrounded by bent metal walls and a sloped catch lip.
      box(door,'coinReturnRecess',.064,.048,.002,x,-.046,.014,black);
      for(const side of [-1,1])box(door,'coinReturnSide',.004,.046,.019,x+side*.032,-.046,.024,steel);
      box(door,'coinReturnTop',.068,.004,.020,x,-.022,.024,steel);
      const lip=box(door,'coinReturnLip',.068,.014,.016,x,-.069,.027,steel);lip.rotation.x=.25;
      label(door,'TOKEN',.039,.007,x,.079,.014);
    }
    label(door,'FREE PLAY',.070,.011,-.006,-.090,.0135);
    this.cabinetButtons={};
    const button=(name,pos,rotation,color,text,radius=.014)=>{
      const root=new THREE.Group();root.name=name+'ButtonAssembly';root.position.copy(pos);root.rotation.y=rotation;cabinet.add(root);
      const cyl=(r,h,z,mat)=>{const m=new THREE.Mesh(new THREE.CylinderGeometry(r,r,h,40),mat);m.rotation.x=Math.PI/2;m.position.z=z;root.add(m);return m;};
      cyl(radius*.83,.032,-.011,black); // through-panel barrel
      cyl(radius*1.23,.003,.0015,black); // mounting washer
      cyl(radius*1.10,.005,.004,chrome); // bezel
      const ring=new THREE.Mesh(new THREE.TorusGeometry(radius,.0018,8,40),black);ring.position.z=.008;root.add(ring);
      const capMat=new THREE.MeshStandardMaterial({color,roughness:.25,metalness:.05,emissive:color,emissiveIntensity:.18});
      const profile=[[0,-.003],[radius*.78,-.003],[radius*.86,-.001],[radius*.86,.002],
        [radius*.82,.004],[radius*.73,.0045],[radius*.61,.0032],[radius*.35,.0023],[0,.002]];
      const verts=[],inds=[];
      for(const [r,y] of profile)for(let j=0;j<=48;j++){const a=j/48*Math.PI*2;verts.push(r*Math.cos(a),y,r*Math.sin(a));}
      for(let i=0;i<profile.length-1;i++)for(let j=0;j<48;j++){const a=i*49+j,b=a+49;inds.push(a,b,a+1,a+1,b,b+1);}
      const geo=new THREE.BufferGeometry();geo.setAttribute('position',new THREE.Float32BufferAttribute(verts,3));geo.setIndex(inds);geo.computeVertexNormals();
      const cap=new THREE.Mesh(geo,capMat);cap.rotation.x=Math.PI/2;cap.position.z=.008;cap.name=name+'ButtonCap';root.add(cap);
      if(text){const art=label(root,text,radius*1.25,radius*.50,0,0,.0112,'#25231a',null);cap.add(art);art.rotation.x=-Math.PI/2;art.position.set(0,.0031,0);}
      this.cabinetButtons[name]={cap,rest:.008};
      return root;
    };
    button('flipperL',new THREE.Vector3(-CAB_SIDE_X-CAB_WALL/2,-.002,TABLE.L/2-.095),-Math.PI/2,0xc33230);
    button('flipperR',new THREE.Vector3(CAB_SIDE_X+CAB_WALL/2,-.002,TABLE.L/2-.095),Math.PI/2,0xc33230);
    button('start',new THREE.Vector3(-.214,-.030,faceZ),0,0xc9df97,'START',.016);
    button('launch',new THREE.Vector3(.214,-.100,faceZ),0,0xf2b340,'LAUNCH',.019);
    label(cabinet,'BALL LAUNCH',.064,.011,.214,-.143,faceZ+.001);
    label(cabinet,'START GAME',.064,.011,-.214,-.073,faceZ+.001);
  }

  async buildCabinet() {
    const cabinetMat = new THREE.MeshStandardMaterial({ color: 0x3d1420, metalness: 0.15, roughness: 0.55 });
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xa6adb2, metalness: 0.85, roughness: 0.32 });
    const cabinet = new THREE.Group();
    cabinet.name = 'cabinet';
    this.cabinetGrp = cabinet;
    this.table.add(cabinet);
    const sideH = CAB_TOP_Y - CAB_BOTTOM_Y;
    const sideL = TABLE.L + 0.1;
    const sideGeo = new THREE.BoxGeometry(CAB_WALL, sideH, sideL);
    // Decals belong on the outer faces only; the inner cabinet has a dark painted finish.
    const sideArtTex = new THREE.CanvasTexture(paintSideArt(window.parent !== window ? 1024 : 2048, window.parent !== window ? 333 : 666));
    sideArtTex.colorSpace = THREE.SRGBColorSpace;
    sideArtTex.anisotropy = this.maxAnisotropy;
    const sideArtMat = new THREE.MeshStandardMaterial({ map: sideArtTex, roughness: 0.5, metalness: 0.1 });
    for (const x of [-CAB_SIDE_X, CAB_SIDE_X]) {
      const innerMat = new THREE.MeshStandardMaterial({color:0x151b20,roughness:.65});
      const side = new THREE.Mesh(sideGeo, [x>0?sideArtMat:innerMat, x<0?sideArtMat:innerMat, cabinetMat, cabinetMat, cabinetMat, cabinetMat]);
      side.position.set(x, (CAB_TOP_Y + CAB_BOTTOM_Y) / 2, 0);
      cabinet.add(side);
    }
    const bottom = new THREE.Mesh(new THREE.BoxGeometry(TABLE.W + CAB_WALL * 2, 0.012, sideL), cabinetMat);
    bottom.position.set(0, CAB_BOTTOM_Y + 0.006, 0);
    cabinet.add(bottom);
    // Front panel (apron face), lockdown bar, rear panel.
    const frontTex=new THREE.CanvasTexture(paintFrontArt(1024,512));frontTex.colorSpace=THREE.SRGBColorSpace;
    frontTex.anisotropy=this.maxAnisotropy;
    const frontArtMat=new THREE.MeshStandardMaterial({map:frontTex,roughness:.52,metalness:.08});
    const front = new THREE.Mesh(new THREE.BoxGeometry(TABLE.W + CAB_WALL * 2, sideH - 0.04, 0.03),
      [cabinetMat,cabinetMat,cabinetMat,cabinetMat,frontArtMat,cabinetMat]);
    front.position.set(0, (CAB_TOP_Y - 0.04 + CAB_BOTTOM_Y) / 2, CAB_FRONT_Z);
    cabinet.add(front);
    // Continuous retaining assembly: the glass ends at +L/2+.030, under the bar.
    // The deep front return overlaps the cabinet panel; no horizontal sightline into the cabinet.
    const lockdownBar = new THREE.Mesh(roundedPanelGeometry(TABLE.W + 0.060, 0.052, 0.065,.009,.006), chromeMat);
    lockdownBar.name = 'lockdownBar';
    lockdownBar.position.set(0, GLASS_Z - 0.017, TABLE.L / 2 + 0.050);
    cabinet.add(lockdownBar);
    const gasketMat = new THREE.MeshStandardMaterial({color:0x141817,roughness:.9});
    const frontGasket = new THREE.Mesh(new THREE.BoxGeometry(TABLE.W + .024,.006,.018),gasketMat);
    frontGasket.name = 'frontGlassGasket';
    frontGasket.position.set(0,GLASS_Z-.001,TABLE.L/2+.027);
    cabinet.add(frontGasket);
    for (const side of [-1,1]) {
      const retainer = new THREE.Mesh(new THREE.BoxGeometry(.016,.009,TABLE.L+.064),chromeMat);
      retainer.name = 'glassSideRetainer';
      retainer.position.set(side*(TABLE.W/2+.010),GLASS_Z+.002,0);
      const seal = new THREE.Mesh(new THREE.BoxGeometry(.013,.004,TABLE.L+.064),gasketMat);
      seal.position.set(side*(TABLE.W/2+.006),GLASS_Z-.002,0);
      cabinet.add(retainer,seal);
    }
    const rearGasket = new THREE.Mesh(new THREE.BoxGeometry(TABLE.W+.026,.008,.023),gasketMat);
    rearGasket.position.set(0,GLASS_Z,-TABLE.L/2-.024);
    cabinet.add(rearGasket);
    const rear = new THREE.Mesh(new THREE.BoxGeometry(TABLE.W + CAB_WALL * 2, sideH, 0.03), cabinetMat);
    rear.position.set(0, (CAB_TOP_Y + CAB_BOTTOM_Y) / 2, CAB_REAR_Z);
    cabinet.add(rear);
    // Pink neon strips along the top of both side walls.
    const neonStripMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.5, 0.35, 0.42) });
    for (const dir of [-1, 1]) {
      const strip = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.006, sideL), neonStripMat);
      strip.position.set(dir * CAB_SIDE_X, CAB_TOP_Y + 0.003, 0);
      cabinet.add(strip);
    }
    await GameLoading.paint();
    this.buildCabinetHardware(cabinet);
    await GameLoading.paint();

    // Folded steel angle legs bolt over each cabinet corner, splay outward below
    // the body, and end in threaded levellers with broad floor pads.
    this.legs = new THREE.Group();
    this.legs.name = 'legs';
    const legMat = new THREE.MeshStandardMaterial({ color: 0xb3bac4, metalness: 0.82, roughness: 0.28 });
    const footMat = new THREE.MeshStandardMaterial({ color: 0x262830, metalness: 0.35, roughness: 0.5 });
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      const top = new THREE.Vector3(sx * (CAB_SIDE_X + CAB_WALL / 2), CAB_BOTTOM_Y + 0.15,
        sz > 0 ? CAB_FRONT_Z + 0.016 : CAB_REAR_Z - 0.016).applyMatrix4(this.tableMatrix);
      const foot = new THREE.Vector3(top.x + sx * 0.055, 0.040, top.z + sz * 0.065);
      const axis = top.clone().sub(foot), length = axis.length();
      const leg = new THREE.Group();
      leg.name = 'cabinetLeg';
      leg.position.copy(foot);
      leg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis.normalize());
      for (const side of [0, 1]) {
        const face = new THREE.Mesh(new THREE.BoxGeometry(side ? 0.003 : 0.042, length, side ? 0.042 : 0.003), legMat);
        face.position.set(side ? 0 : -sx * 0.020, length / 2, side ? -sz * 0.020 : 0);
        leg.add(face);
      }
      for (const offset of [0.032, 0.092]) {
        const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.005, 6), legMat);
        bolt.rotation.x = Math.PI / 2;
        bolt.position.set(-sx * 0.019, length - offset, sz * 0.004);
        bolt.name = 'legBolt';
        leg.add(bolt);
      }
      this.legs.add(leg);
      const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.005, 0.035, 12), legMat);
      stem.name = 'legLeveller';
      stem.position.set(foot.x, 0.030, foot.z);
      const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.020, 0.025, 0.014, 24), footMat);
      pad.position.set(foot.x, 0.007, foot.z);
      this.legs.add(stem, pad);
      for (let j = 0; j < 6; j++) {
        const thread = new THREE.Mesh(new THREE.TorusGeometry(0.005, 0.0007, 4, 12), legMat);
        thread.rotation.x = Math.PI / 2;
        thread.position.set(foot.x, 0.019 + j * 0.004, foot.z);
        this.legs.add(thread);
      }
    }
    this.machineRoot.add(this.legs);

    // Backbox: upright on a neck at the cabinet's rear top edge.
    const rearTop = new THREE.Vector3(0, CAB_TOP_Y, CAB_REAR_Z + 0.015).applyMatrix4(this.tableMatrix);
    const neck = new THREE.Mesh(new THREE.BoxGeometry(BACKBOX_W - 0.04, NECK_H + 0.03, BACKBOX_D - 0.02), cabinetMat);
    neck.name = 'neck';
    neck.position.set(0, rearTop.y + (NECK_H - 0.03) / 2, rearTop.z - 0.055);
    this.machineRoot.add(neck);
    const backbox = new THREE.Group();
    backbox.name = 'backbox';
    backbox.position.set(0, rearTop.y + NECK_H + BACKBOX_H / 2, rearTop.z - 0.055);
    const backboxBody = new THREE.Mesh(new THREE.BoxGeometry(BACKBOX_W, BACKBOX_H, BACKBOX_D), cabinetMat);
    backbox.add(backboxBody);
    const frontZ = BACKBOX_D / 2;
    // Translite: the painted backglass (art.js) shown immediately, the lit X-Arcade logo composited on top once
    // the embedded PNG has decoded. Canvas aspect = plane aspect (0.51 x 0.33), so nothing is stretched.
    const transW = 1024;
    const transH = 662;
    this.transCanvas = paintTranslite(transW, transH);
    this.transTex = new THREE.CanvasTexture(this.transCanvas);
    this.transTex.colorSpace = THREE.SRGBColorSpace;
    this.transTex.anisotropy = this.maxAnisotropy;
    this.transMat = new THREE.MeshBasicMaterial({ map: this.transTex, color: new THREE.Color(1, 1, 1) });
    const translite = new THREE.Mesh(new THREE.PlaneGeometry(TABLE.W + 0.01, 0.33), this.transMat);
    translite.name = 'translite';
    translite.position.set(0, 0.045, frontZ + 0.002);
    backbox.add(translite);
    const logoImage = new Image();
    this.assetsReady = new Promise((resolve, reject) => {
      logoImage.addEventListener('load', async () => {
        try {
          if (logoImage.decode) await logoImage.decode();
          this.compositeLogo(logoImage);
          resolve();
        } catch (error) { reject(error); }
      }, {once: true});
      logoImage.addEventListener('error', () => reject(new Error('Controller artwork could not be decoded')), {once: true});
    });
    logoImage.src = XARCADE_LOGO_PNG;
    // Speaker panel with the DMD in a dark bezel. The 128x32 canvas is drawn by dmd.js; the shader turns each
    // texel into a round orange phosphor dot.
    const speakerPanel = new THREE.Mesh(
      new THREE.BoxGeometry(BACKBOX_W - 0.01, 0.13, 0.008),
      new THREE.MeshStandardMaterial({ color: 0x0f0d14, metalness: 0.3, roughness: 0.7 }),
    );
    speakerPanel.position.set(0, -0.165, frontZ + 0.002);
    backbox.add(speakerPanel);
    const bezel = new THREE.Mesh(
      new THREE.BoxGeometry(0.44, 0.13, 0.014),
      new THREE.MeshStandardMaterial({ color: 0x0b0b10, metalness: 0.55, roughness: 0.45 }),
    );
    bezel.name = 'dmdBezel';
    bezel.position.set(0, -0.165, frontZ + 0.008);
    backbox.add(bezel);
    const bezelLip = new THREE.Mesh(new THREE.BoxGeometry(0.418, 0.112, 0.004), chromeMat);
    bezelLip.position.set(0, -0.165, frontZ + 0.016);
    backbox.add(bezelLip);
    this.dmdCanvas = document.createElement('canvas');
    this.dmdCanvas.width = 128;
    this.dmdCanvas.height = 32;
    this.dmdTex = new THREE.CanvasTexture(this.dmdCanvas);
    this.dmdTex.minFilter = THREE.NearestFilter;
    this.dmdTex.magFilter = THREE.NearestFilter;
    this.dmdMat = new THREE.ShaderMaterial({
      uniforms: { tex: { value: this.dmdTex }, time: { value: 0 } },
      vertexShader: `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `
        uniform sampler2D tex; uniform float time;
        varying vec2 vUv;
        void main() {
          vec2 res = vec2(128.0, 32.0);
          vec2 uvRes = vUv * res;
          vec2 cell = floor(uvRes);
          vec2 f = fract(uvRes);
          // Screen pixels one dot cell covers. Under ~3 px the round-dot mask undersamples and the UNLIT cells
          // turn into a bright moire maze with more contrast than the glyphs, so both the mask and the off-dot
          // ambient fade out and the panel degrades to a clean glow instead of a labyrinth.
          float cellPx = 1.0 / max(max(fwidth(uvRes.x), fwidth(uvRes.y)), 1e-5);
          float sharp = smoothstep(1.5, 3.0, cellPx);
          float sharpDot = texture2D(tex, (cell + 0.5) / res).r;
          // Bilinear average over the four nearest dot centres: what the panel looks like once dots stop resolving.
          vec2 b = uvRes - 0.5;
          vec2 bi = floor(b);
          vec2 bf = fract(b);
          float s00 = texture2D(tex, (bi + vec2(0.5, 0.5)) / res).r;
          float s10 = texture2D(tex, (bi + vec2(1.5, 0.5)) / res).r;
          float s01 = texture2D(tex, (bi + vec2(0.5, 1.5)) / res).r;
          float s11 = texture2D(tex, (bi + vec2(1.5, 1.5)) / res).r;
          float s = mix(mix(mix(s00, s10, bf.x), mix(s01, s11, bf.x), bf.y), sharpDot, sharp);
          float d = mix(1.0, 1.0 - smoothstep(0.30, 0.46, length(f - 0.5)), sharp);
          float flicker = 0.96 + 0.04 * sin(time * 47.0 + cell.x * 0.7);
          vec3 phosphor = mix(vec3(1.0, 0.32, 0.04), vec3(1.0, 0.72, 0.25), s);
          vec3 col = phosphor * s * d * 2.6 * flicker + vec3(0.6, 0.2, 0.05) * d * 0.055 * sharp;
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const dmdPlane = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.1), this.dmdMat);
    dmdPlane.name = 'dmd';
    dmdPlane.position.set(0, -0.165, frontZ + 0.0185);
    backbox.add(dmdPlane);
    this.backbox = backbox;
    this.machineRoot.add(backbox);
    this.dmdCorners = [
      [-0.2, -0.115],
      [0.2, -0.115],
      [-0.2, -0.215],
      [0.2, -0.215],
    ].map(([dx, dy]) => new THREE.Vector3(dx, dy, frontZ + 0.02).add(backbox.position));
  }

  /** Blend the lit logo sprite onto the painted translite, centred and letterboxed at 76% of the panel width. */
  compositeLogo(image) {
    const { canvas, box } = litLogoSprite(image);
    const ctx = this.transCanvas.getContext('2d');
    const targetW = this.transCanvas.width * 0.76;
    const scale = targetW / box.w;
    const targetH = box.h * scale;
    const x = (this.transCanvas.width - targetW) / 2;
    const y = this.transCanvas.height * 0.29 - targetH / 2;
    ctx.save();
    ctx.shadowColor = 'rgba(255,255,255,0.55)';
    ctx.shadowBlur = 22;
    ctx.drawImage(canvas, box.x, box.y, box.w, box.h, x, y, targetW, targetH);
    ctx.restore();
    this.transTex.needsUpdate = true;
  }

  /**
   * The room the machine stands in: a floor with a pool of light under the machine and a faint tile grid, a back
   * wall carrying two neon strips, a second wall behind the player (so orbiting round the machine never looks into
   * the void) and a soft contact shadow so the cabinet is planted on the floor rather than floating.
   * ROOM_HALF_Z is the wall distance the machine viewer's zoom limit is derived from.
   */
  buildRoom() {
    const floorCanvas = document.createElement('canvas');
    floorCanvas.width = floorCanvas.height = 512;
    const floorCtx = floorCanvas.getContext('2d');
    // A pool of light a few metres wide around the machine (the plane is 16 m across, so the floor still fades
    // into the background colour long before its edge).
    const floorGradient = floorCtx.createRadialGradient(256, 256, 10, 256, 256, 244);
    floorGradient.addColorStop(0, '#332b40');
    floorGradient.addColorStop(0.3, '#1e1a2b');
    floorGradient.addColorStop(0.62, '#0e0c15');
    floorGradient.addColorStop(1, '#040409');
    floorCtx.fillStyle = floorGradient;
    floorCtx.fillRect(0, 0, 512, 512);
    floorCtx.strokeStyle = 'rgba(150,160,200,0.055)';
    for (let i = 0; i <= 32; i++) {
      floorCtx.beginPath();
      floorCtx.moveTo(i * 16, 0);
      floorCtx.lineTo(i * 16, 512);
      floorCtx.stroke();
      floorCtx.beginPath();
      floorCtx.moveTo(0, i * 16);
      floorCtx.lineTo(512, i * 16);
      floorCtx.stroke();
    }
    const floorTex = new THREE.CanvasTexture(floorCanvas);
    floorTex.colorSpace = THREE.SRGBColorSpace;
    floorTex.anisotropy = this.maxAnisotropy;
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(16, 16),
      // Nearly non-metallic: the painted pool of light should read, not the studio env map.
      new THREE.MeshStandardMaterial({ map: floorTex, roughness: 0.6, metalness: 0.1, envMapIntensity: 0.22 }),
    );
    floor.name = 'floor';
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    // Contact shadow: a soft dark ellipse on the floor under the cabinet and its legs.
    const shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1.05, 1.85),
      new THREE.MeshBasicMaterial({
        map: new THREE.CanvasTexture(makeBlobTexture(128, 'rgba(0,0,0,0.9)')),
        color: 0,
        transparent: true,
        opacity: 0.7,
        depthWrite: false,
      }),
    );
    shadow.name = 'machineShadow';
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.set(0, 0.004, 0.05);
    this.scene.add(shadow);
    // Walls: a dark vertical gradient (lighter where it meets the floor) keeps them from reading as pure void.
    const wallCanvas = document.createElement('canvas');
    wallCanvas.width = 8;
    wallCanvas.height = 256;
    const wallCtx = wallCanvas.getContext('2d');
    const wallGradient = wallCtx.createLinearGradient(0, 0, 0, 256);
    wallGradient.addColorStop(0, '#0a0810');
    wallGradient.addColorStop(0.55, '#141020');
    wallGradient.addColorStop(1, '#221a2e');
    wallCtx.fillStyle = wallGradient;
    wallCtx.fillRect(0, 0, 8, 256);
    const wallTex = new THREE.CanvasTexture(wallCanvas);
    wallTex.colorSpace = THREE.SRGBColorSpace;
    const wallMat = new THREE.MeshStandardMaterial({ map: wallTex, roughness: 0.92, metalness: 0.05 });
    const wallGeo = new THREE.PlaneGeometry(16, 4.4);
    for (const [z, rotation, name] of [
      [-Renderer.ROOM_HALF_Z, 0, 'backWall'],
      [Renderer.ROOM_HALF_Z, Math.PI, 'frontWall'],
    ]) {
      const wall = new THREE.Mesh(wallGeo, wallMat);
      wall.name = name;
      wall.position.set(0, 2.2, z);
      wall.rotation.y = rotation;
      this.scene.add(wall);
    }
    // Two thin vertical neon strips on the back wall, flanking the machine (magenta left, cyan right).
    for (const [x, color] of [
      [-1.5, 0xff2fa8],
      [1.5, 0x19e3ff],
    ]) {
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(0.024, 2.3, 0.024),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(1.5) }),
      );
      strip.name = 'wallNeon';
      strip.position.set(x, 1.5, -Renderer.ROOM_HALF_Z + 0.02);
      this.scene.add(strip);
    }
  }

  /**
   * Lamp inserts as two InstancedMeshes: flat tinted translucent discs on the art (lit up by their instance colour
   * when the lamp is on) and additive glow sprites above them. `lampDomes` keeps its name for API parity.
   */
  buildLampInstances() {
    const defs = this.T.lamps;
    this.lampDefs = defs;
    const count = defs.length;
    const glowTex = new THREE.CanvasTexture(makeBlobTexture(64));
    const glowGeo = new THREE.PlaneGeometry(0.03, 0.03);
    glowGeo.rotateX(-Math.PI / 2);
    const glowMat = new THREE.MeshBasicMaterial({
      map: glowTex,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.lampGlows = new THREE.InstancedMesh(glowGeo, glowMat, count);
    this.lampGlows.name = 'lampGlows';
    const insertGeo = new THREE.CircleGeometry(1, 16);
    insertGeo.rotateX(-Math.PI / 2);
    const insertMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    this.lampDomes = new THREE.InstancedMesh(insertGeo, insertMat, count);
    this.lampDomes.name = 'lampInserts';
    const matrix = new THREE.Matrix4();
    defs.forEach((lamp, i) => {
      // Halo sized with the insert: big inserts and GI lamps spread, small ones stay tight (a lit white insert
      // with a full-size halo reads as a loose ball on the playfield).
      const glowSize = lamp.big ? 1.7 : lamp.gi ? 1.5 : lamp.small ? 0.62 : 0.86;
      matrix.makeScale(glowSize, 1, glowSize).setPosition(toWorld(lamp.x, lamp.y, 0.0035));
      this.lampGlows.setMatrixAt(i, matrix);
      const radius =
        lamp.gi ? 0.004 : lamp.shape === 'arrow' ? 0.0095 : lamp.big ? 0.0118 : lamp.small ? 0.0058 : 0.0082;
      matrix.makeScale(radius, 1, radius).setPosition(toWorld(lamp.x, lamp.y, 0.0007));
      this.lampDomes.setMatrixAt(i, matrix);
      this.lampGlows.setColorAt(i, this.tmpColor.set(0, 0, 0));
      this.lampDomes.setColorAt(i, this.tmpColor.set(lampColor(lamp.color)).multiplyScalar(0.18));
    });
    this.table.add(this.lampGlows, this.lampDomes);
  }

  /**
   * Particle pool: 320 additive sprites in one Points object. pData holds 8 floats per particle
   * (x, y, z, vx, vy, vz, life, decay); pColor the base RGB. Dead particles are parked at y = -10.
   */
  buildParticles() {
    this.pMax = 320;
    this.pData = new Float32Array(320 * 8);
    this.pColor = new Float32Array(320 * 3);
    const geometry = new THREE.BufferGeometry();
    this.pPos = new THREE.Float32BufferAttribute(new Float32Array(320 * 3), 3);
    this.pCol = new THREE.Float32BufferAttribute(new Float32Array(320 * 3), 3);
    geometry.setAttribute('position', this.pPos);
    geometry.setAttribute('color', this.pCol);
    const spriteTex = new THREE.CanvasTexture(makeBlobTexture(32));
    const material = new THREE.PointsMaterial({
      size: 0.011,
      map: spriteTex,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.points = new THREE.Points(geometry, material);
    this.points.name = 'particles';
    this.points.frustumCulled = false;
    this.table.add(this.points);
    this.pHead = 0;
  }

  /**
   * World-space points the camera views must keep on screen: playfield corners (at the surface and at glass
   * height), the lockbar's front edge, the DMD corners and the backbox top; plus the machine's corners for the
   * orbit viewer. Computed once, after the whole machine exists.
   */
  buildFitPoints() {
    const W = TABLE.W;
    const L = TABLE.L;
    const pt = (x, y, z) => this.fw(x, y, z);
    this.fitTable = [pt(0, 0, 0), pt(W, 0, 0), pt(0, L, 0), pt(W, L, 0), pt(0, 0, GLASS_Z), pt(W, 0, GLASS_Z),
      pt(0, L, GLASS_Z), pt(W, L, GLASS_Z),
      new THREE.Vector3(-W / 2 - 0.026, CAB_TOP_Y, TABLE.L / 2 + 0.075).applyMatrix4(this.tableMatrix),
      new THREE.Vector3(W / 2 + 0.026, CAB_TOP_Y, TABLE.L / 2 + 0.075).applyMatrix4(this.tableMatrix)];
    this.fitTableDmd = this.fitTable.concat(this.dmdCorners);
    const centroid = (points) => {
      const c = new THREE.Vector3();
      for (const p of points) c.add(p);
      return c.multiplyScalar(1 / points.length);
    };
    this.centreTable = centroid(this.fitTable);
    this.centreTableDmd = centroid(this.fitTableDmd);
    // The DMD panel on its own: the INITIALS close-up pose fills the frame with it.
    this.fitDmd = this.dmdCorners.slice();
    this.centreDmd = centroid(this.fitDmd);
    this.scene.updateMatrixWorld(true);
    // The whole machine front (playfield + DMD + the translite above it). Tall frames have the room to show it;
    // wide ones do not, which is what the tiers in fitPlayView() are for.
    const backboxBox = new THREE.Box3().setFromObject(this.backbox);
    this.fitMachineFront = this.fitTableDmd.concat([
      new THREE.Vector3(backboxBox.min.x, backboxBox.max.y, backboxBox.max.z),
      new THREE.Vector3(backboxBox.max.x, backboxBox.max.y, backboxBox.max.z),
    ]);
    // Orbit viewer + attract: the machine's bounding box (cabinet + legs + backbox; particles are excluded).
    const box = new THREE.Box3().setFromObject(this.cabinetGrp);
    box.expandByObject(this.legs);
    box.expandByObject(this.backbox);
    box.getCenter(this.machineTarget);
    this.machineCorners = [];
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) this.machineCorners.push(new THREE.Vector3(x, y, z));
      }
    }
    // Attract frames the whole machine, legs included, so the sweep never cuts the cabinet off at the bottom.
    this.fitMachine = this.fitTableDmd.concat(this.machineCorners);
    this.centreMachine = centroid(this.machineCorners);
  }

  /** Effect age follows simulation time, so pause/replay cannot leave it stuck on. */
  updateArcadeCelebration(now,game) {
    const effect=this.arcadeCelebration,age=now-effect.started;
    const visible=age>=0&&age<effect.duration&&game.state!=='ATTRACT'&&game.state!=='OVER';
    effect.group.visible=visible;if(!visible){effect.glow.intensity=0;return;}
    const u=age/effect.duration,fade=Math.pow(1-u,1.4),strength=effect.strength;
    for(let i=0;i<effect.shards.length;i++){
      const {mesh,angle,speed,lift}=effect.shards[i],radius=(.008+speed*age)*strength;
      mesh.position.set(Math.cos(angle)*radius,Math.sin(angle)*radius*.55+lift*age-.027*age*age,Math.sin(angle*1.7)*radius*.3);
      mesh.rotation.set(age*(2+i%3),age*(1+i%5),angle+age*2);
      mesh.material.opacity=fade;mesh.scale.setScalar(.65+fade*.7);
    }
    for(let i=0;i<effect.rings.length;i++){
      const ring=effect.rings[i],t=Math.max(0,age-i*.10);
      ring.scale.setScalar(1+t*(i?3.2:4.8)*strength);ring.material.opacity=fade*.65;
    }
    effect.glow.intensity=.07*strength*Math.exp(-age*5);
  }

  /**
   * Spawn `count` sparks at table point (x, y). Consumes Math.random() 5 times per particle in this exact order
   * (angle, speed, height jitter, upward speed, decay) — the deterministic replay tests depend on it.
   */
  burst(x, y, color, count = 16, speedScale = 1) {
    const base = this.tmpColor2.set(color);
    const origin = toWorld(x, y, 0.012);
    for (let n = 0; n < count; n++) {
      const slot = (this.pHead = (this.pHead + 1) % this.pMax);
      const offset = slot * 8;
      const angle = Math.random() * Math.PI * 2;
      const speed = (0.15 + Math.random() * 0.55) * speedScale;
      this.pData[offset] = origin.x;
      this.pData[offset + 1] = origin.y + Math.random() * 0.02;
      this.pData[offset + 2] = origin.z;
      this.pData[offset + 3] = Math.cos(angle) * speed;
      this.pData[offset + 4] = 0.3 + Math.random() * 0.5;
      this.pData[offset + 5] = Math.sin(angle) * speed * 0.5;
      this.pData[offset + 6] = 1;
      this.pData[offset + 7] = 1.6 + Math.random() * 1.6;
      this.pColor[slot * 3] = base.r * 2.2;
      this.pColor[slot * 3 + 1] = base.g * 2.2;
      this.pColor[slot * 3 + 2] = base.b * 2.2;
    }
  }

  /** Integrate the particle pool: gravity 1.6 m/s^2 in table space, colour fades with remaining life. */
  updateParticles(dt) {
    const positions = this.pPos.array;
    const colors = this.pCol.array;
    for (let i = 0; i < this.pMax; i++) {
      const offset = i * 8;
      let life = this.pData[offset + 6];
      if (life <= 0) {
        positions[i * 3 + 1] = -10;
        continue;
      }
      life -= dt * this.pData[offset + 7];
      this.pData[offset + 6] = life;
      this.pData[offset + 4] -= dt * 1.6;
      this.pData[offset] += this.pData[offset + 3] * dt;
      this.pData[offset + 1] += this.pData[offset + 4] * dt;
      this.pData[offset + 2] += this.pData[offset + 5] * dt;
      positions[i * 3] = this.pData[offset];
      positions[i * 3 + 1] = this.pData[offset + 1];
      positions[i * 3 + 2] = this.pData[offset + 2];
      const fade = Math.max(0, life);
      colors[i * 3] = this.pColor[i * 3] * fade;
      colors[i * 3 + 1] = this.pColor[i * 3 + 1] * fade;
      colors[i * 3 + 2] = this.pColor[i * 3 + 2] * fade;
    }
    this.pPos.needsUpdate = true;
    this.pCol.needsUpdate = true;
  }

  /** Table point (x, y, height z) -> world space through the fixed table matrix. `out` avoids an allocation. */
  fw(x, y, z, out) {
    return (out || new THREE.Vector3()).set(x - TABLE.W / 2, z, TABLE.L / 2 - y).applyMatrix4(this.tableMatrix);
  }

  /**
   * Size the canvas at DPR (capped at 2) times the adaptive renderScale; the post chain follows. Portrait
   * viewports get a taller field of view so the fitted views do not have to back off as far.
   */
  resize(width, height) {
    const scale = Math.min(window.devicePixelRatio || 1, window.parent !== window ? 1.25 : 2) * this.renderScale;
    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(1);
    const bufferW = Math.max(2, Math.round(width * scale));
    const bufferH = Math.max(2, Math.round(height * scale));
    this.renderer.domElement.width = bufferW;
    this.renderer.domElement.height = bufferH;
    this.renderer.setViewport(0, 0, bufferW, bufferH);
    this.post.setSize(bufferW, bufferH);
    this.camera.aspect = width / height;
    this.camera.fov = this.camera.aspect < 1 ? 52 : 40;
    this.camera.updateProjectionMatrix();
    this.w = width;
    this.h = height;
  }

  /** Visual side of a game fx event (main routes every non-sound/DMD/score fx here). */
  handleFx(fx) {
    switch (fx.type) {
      case 'shake':
        this.shake.mag = Math.min(1.2, this.shake.mag + fx.mag);
        this.shake.dx = fx.dx || 0;
        this.shake.dy = fx.dy || 0;
        break;
      case 'burst':
        this.burst(fx.x, fx.y, fx.color, fx.n || 16);
        break;
      case 'popFire': {
        const pop = this.pops[fx.i];
        if (pop) pop.pulse = 1;
        this.burst(this.T.meta.pops[fx.i][0], this.T.meta.pops[fx.i][1], 0xffb020, 10, 0.7);
        break;
      }
      case 'slingFire': {
        const sling = this.slings[fx.side];
        if (sling) sling.pulse = 1;
        break;
      }
      case 'dropDown':
        // Nudge off zero so the ease in render() starts moving immediately.
        this.dropAnim[fx.i] = 0.001;
        break;
      case 'drops':
        if (fx.up) this.dropAnim = this.dropAnim.map(() => -0.001);
        break;
      case 'spinTick':
        this.spinnerVel = Math.min(40, this.spinnerVel + 9);
        break;
      case 'plunger':
        this.plungerPower = fx.power;
        break;
      case 'cabinetCelebrationEnd':
        this.arcadeCelebration.started=-10;this.arcadeCelebration.label='';break;
      case 'cabinetCelebration': {
        const fxState=this.arcadeCelebration;
        fxState.started=this.T.world.time;fxState.label=fx.label;fxState.strength=fx.strength||1;
        break;
      }
      case 'kickbackFire':
        if(this.kickbackUnits?.[fx.side])this.kickbackUnits[fx.side].pulse=1;
        // A mechanical strike has a lamp pulse, not an explosion at the ball.
        break;
      case 'ballLocked':
        this.burst(0.27, 0.64, 0xffb020, 26, 1.1);
        break;
      case 'ballHit': {
        // Ball-ball click: a few white sparks between the two balls (rules may pass x/y or the ball records).
        const x = fx.x ?? (fx.a && fx.b ? (fx.a.p.x + fx.b.p.x) / 2 : null);
        const y = fx.y ?? (fx.a && fx.b ? (fx.a.p.y + fx.b.p.y) / 2 : null);
        if (x !== null && y !== null) this.burst(x, y, 0xdfe8ff, 6, 0.6);
        break;
      }
      case 'flash':
        this.burst(fx.x, fx.y, fx.color, 12, 0.8);
        break;
      case 'cam':
        if (fx.name === 'multiball') this.camKick = 1;
        break;
    }
  }

  /** Take a ball visual from the pool (or build one from the shared geometry/materials) and attach it. */
  acquireBallVisual() {
    let visual = this.ballPool.pop();
    if (!visual) {
      const mesh = new THREE.Mesh(this.ballGeo, this.ballMat);
      const blob = new THREE.Mesh(
        this.blobGeo,
        new THREE.MeshBasicMaterial({
          map: this.blobTex,
          color: 0,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
        }),
      );
      visual = { mesh, blob };
    }
    this.table.add(visual.mesh, visual.blob);
    return visual;
  }

  /**
   * Draw one frame. `alpha` interpolates between `prev` (ball positions / flipper angles captured before the last
   * physics step) and the current world state; `game` supplies state/multiball for camera and marquee.
   * While the machine view is active the call only records its arguments (the viewer's own loop draws).
   */
  render(dt, world, game, alpha, prev) {
    this.lastFrame.world = world;
    this.lastFrame.game = game;
    this.lastFrame.alpha = alpha;
    this.lastFrame.prev = prev;
    if (this.machineView.active) return;
    this.time += dt;
    this.uptime += dt;
    const now = this.time;

    // Balls: chrome sphere + contact-shadow blob from a shared pool; hidden balls (in the scoop) are skipped.
    const liveIds = this.liveIds;
    liveIds.clear();
    for (const ball of world.balls) {
      if (!ball.active || ball.hidden) continue;
      liveIds.add(ball.id);
      let visual = this.ballMeshes.get(ball.id);
      if (!visual) {
        visual = this.acquireBallVisual();
        this.ballMeshes.set(ball.id, visual);
      }
      const from = (prev && prev.balls.get(ball.id)) || ball.p;
      const fromZ = prev && prev.balls.get(ball.id) ? from.z : ball.z;
      const x = from.x + (ball.p.x - from.x) * alpha;
      const y = from.y + (ball.p.y - from.y) * alpha;
      const z = fromZ + (ball.z - fromZ) * alpha;
      visual.mesh.position.set(x - TABLE.W / 2, z + ball.r, TABLE.L / 2 - y);
      // Roll the sphere by distance / radius (0.9 fudge keeps the reflection motion readable); angles wrap.
      visual.mesh.rotation.z = (visual.mesh.rotation.z - ((ball.v.x * dt) / ball.r) * 0.9) % (Math.PI * 2);
      visual.mesh.rotation.x = (visual.mesh.rotation.x - ((ball.v.y * dt) / ball.r) * 0.9) % (Math.PI * 2);
      visual.blob.position.set(x - TABLE.W / 2, 0.0022, TABLE.L / 2 - y);
      // Shadow spreads and fades as the ball climbs a ramp.
      const shadowStrength = Math.max(0.25, 1 - z * 9);
      visual.blob.scale.setScalar(1 + z * 7);
      visual.blob.material.opacity = 0.5 * shadowStrength;
    }
    for (const [id, visual] of this.ballMeshes) {
      if (liveIds.has(id)) continue;
      this.table.remove(visual.mesh, visual.blob);
      this.ballPool.push(visual);
      this.ballMeshes.delete(id);
    }

    // Flippers interpolate between the previous and current collider angle (the upper one included).
    for (const side of ['L', 'R', 'U']) {
      const entry = this.flippers[side];
      if (!entry) continue;
      const flipper = entry.f;
      const previousAngle = prev && prev.flippers[side] != null ? prev.flippers[side] : flipper.angle;
      entry.grp.rotation.y = previousAngle + (flipper.angle - previousAngle) * alpha;
    }

    // Solenoid pulls the metal bumper ring downward; the fixed body never changes size.
    for (const pop of this.pops) {
      if (pop.pulse > 0) {
        pop.pulse = Math.max(0, pop.pulse - dt * 6);
        pop.ring.position.y = 0.022 - pop.pulse * 0.006;
        pop.capMat.emissiveIntensity = 0.25 + pop.pulse * 3.2;
      }
    }
    for(const side of ['L','R']){
      const k=this.kickbackUnits[side];k.pulse=Math.max(0,k.pulse-dt*10);
      const seated=world.balls.find(b=>b.active&&!b.held&&!b.rampId&&Math.abs(b.p.x-(side==='L'?.0195:.431))<.008&&b.p.y<.163&&b.p.y>.15);
      const dwell=seated?seated._kickT||0:0;
      const retract=Math.min(1,dwell/.18)*.004;
      const stroke=clamp((dwell-.18)/.04,0,1)*.010;
      k.rod.position.z=k.restZ+retract-stroke-k.pulse*.006;
      k.lamp.material.color.setHex(dwell>0?0xffc56b:game['kickbackArmed'+side]?0x21e6ce:0x292a30);
      k.door.position.y=this.T.meta.kickDoors[side].active?.009:-.012;

    }
    // Sling rubber deflects very slightly; keep the mechanical body silhouette stable.
    for (const side of ['L', 'R']) {
      const sling = this.slings[side];
      if (sling.pulse > 0) {
        sling.pulse = Math.max(0, sling.pulse - dt * 7);
        sling.grp.scale.y = 1 + sling.pulse * 0.035;
      }
    }
    // Drop targets ease toward down (1) when their collider is inactive, up (0) when reset.
    for (let i = 0; i < this.drops.length; i++) {
      const goal = this.T.meta.drops[i].active ? 0 : 1;
      let anim = this.dropAnim[i];
      anim += (goal - anim) * Math.min(1, dt * 14);
      this.dropAnim[i] = anim;
      this.drops[i].mesh.position.y = this.drops[i].baseY - anim * 0.0225;
    }
    // Spinner: each tick adds angular velocity, friction bleeds it off.
    this.spinnerVel = Math.max(0, this.spinnerVel - dt * 18);
    this.spinnerRot += this.spinnerVel * dt;
    this.spinnerHinge.rotation.x = this.spinnerRot;
    // Gate swings open while any ball is in the orbit entry region behind it.
    let ballAtGate = 0;
    for (const ball of world.balls) if (ball.p.y > 0.72 && ball.p.y < 0.82 && ball.p.x > 0.42) ballAtGate = 1;
    this.gateSwing += ((ballAtGate ? 1.1 : 0) - this.gateSwing) * Math.min(1, dt * 10);
    this.gateHinge.rotation.x = this.gateSwing;
    // External button travel follows the same controls as the table mechanisms.
    for (const [name,entry] of Object.entries(this.cabinetButtons)) {
      const down=name==='flipperL'?this.T.meta.flippers.L.pressed:name==='flipperR'?this.T.meta.flippers.R.pressed:name==='launch'?game.plungeHeld:false;
      entry.cap.position.z=entry.rest-(down?.003:0);
    }
    // Plunger pulls back with the held power.
    const pull = game.plungePower * 0.08;
    this.plungerRod.position.z = this.plungerRestZ + pull;
    this.plungerSpring.position.z = this.springRestZ + pull;
    this.plungerSpring.scale.z = (0.13 - pull) / 0.13;

    // Lamp colours from the rules-side Lamps brightness: the insert disc lights up, the glow sprite fades in.
    const tint = this.tmpColor;
    const shade = this.tmpColor2;
    for (let i = 0; i < this.lampDefs.length; i++) {
      const lamp = this.lampDefs[i];
      const brightness = this.lamps.brightness(lamp);
      tint.set(lampColor(lamp.color));
      shade.copy(tint).multiplyScalar(brightness * (lamp.gi ? 0.28 : 0.6));
      this.lampGlows.setColorAt(i, shade);
      // Lit inserts stay under the bloom threshold (1.18) on their brightest channel, so the colour reads before
      // the glare does — at 1.23 the white sling lamps clipped and bloomed over everything around them.
      shade.copy(tint).multiplyScalar(0.16 + brightness * 0.95);
      this.lampDomes.setColorAt(i, shade);
    }
    this.lampGlows.instanceColor.needsUpdate = true;
    this.lampDomes.instanceColor.needsUpdate = true;
    // General illumination level drives the real lights (hemi 0.98, ambient 0.6, key 1.1 at full GI).
    const gi = this.lamps.gi;
    this.ambient.intensity = 0.45 + gi * 0.48;
    this.keyLight.intensity = 0.40 + gi * 0.95;
    this.popLight.intensity = gi * 0.12;
    this.flipLight.intensity = gi * 0.18;
    const returnBall=world.balls.find(b=>b.active&&b.p.y>.26&&b.p.y<.32&&Math.abs(b.p.x-.395)<.016&&b.v.y<0);
    this.returnFlap.rotation.x=returnBall?-1.2*Math.max(0,1-Math.abs(returnBall.p.y-.285)/.035):0;
    this.updateArcadeCelebration(world.time,game);
    // Mini cabinet screen redraws at ~11 Hz while the toy is visible.
    if (this.miniCabGrp.visible) {
      this.msAcc += dt;
      if (this.msAcc > 0.09) {
        this.miniScreen.draw(this.msAcc, game.multiball, this.arcadeCelebration.group.visible?this.arcadeCelebration.label:null);
        this.miniScreenTex.needsUpdate = true;
        this.msAcc = 0;
      }
      this.marqueeMat.color.setScalar(game.multiball ? 1.2 + Math.sin(now * 9) * 0.4 : 1.35);
    }
    this.updateParticles(dt);
    this.dmdMat.uniforms.time.value = now;
    this.updateCamera(dt, world, game, now);
    this.updateRenderScale(dt);
    this.post.render(this.scene, this.camera, now);
  }

  /**
   * Adaptive resolution. Frame intervals are compared with the display's refresh period (the shortest interval
   * seen): when the median of the last 48 frames runs over 1.5x that period (and above 19 ms) the scale steps
   * down; when frames are back at the display rate for 3 s it steps up again. The first 2 s (shader compiles)
   * and single hitches over 100 ms are ignored.
   */
  updateRenderScale(dt) {
    if (this.uptime < 2 || dt > 0.1 || dt <= 0) return;
    this.displayInterval = Math.max(1 / 240, Math.min(this.displayInterval, dt));
    this.frameSamples[this.frameSampleCount++] = dt;
    if (this.frameSampleCount < this.frameSamples.length) return;
    this.frameSampleCount = 0;
    const sorted = Array.from(this.frameSamples).sort((a, b) => a - b);
    const median = sorted[sorted.length >> 1];
    const period = this.displayInterval;
    if (median > Math.max(period * 1.5, 0.019) && this.renderScale > 0.68) {
      this.renderScale = Math.max(0.68, this.renderScale - 0.08);
      this.scaleChangedAt = this.uptime;
      this.resize(this.w, this.h);
    } else if (median < period * 1.15 && this.renderScale < 1 && this.uptime - this.scaleChangedAt > 3) {
      this.renderScale = Math.min(1, this.renderScale + 0.05);
      this.scaleChangedAt = this.uptime;
      this.resize(this.w, this.h);
    }
  }

  /**
   * Distance along `dir` (unit vector from `target` toward the camera) at which every point in `points` fits in
   * the current frustum (both the vertical and the horizontal field of view), with a relative `margin`.
   * The camera keeps looking at `target`, so this is the right fit for a set that is symmetric about it (the
   * machine box, the DMD panel). frameFit() is the variant that also recentres what it fits.
   */
  fitDistance(target, dir, points, margin = 1.05) {
    const forward = this.tmpDir.copy(dir).negate();
    const right = this.tmpRight.crossVectors(forward, Renderer.UP);
    if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
    right.normalize();
    const up = this.tmpUp.crossVectors(right, forward);
    const tanV = Math.tan((this.camera.fov * Math.PI) / 360);
    const tanH = tanV * this.camera.aspect;
    let distance = 0.2;
    const q = this.tmpV2;
    for (const p of points) {
      q.copy(p).sub(target);
      const x = Math.abs(q.dot(right)) * margin;
      const y = Math.abs(q.dot(up)) * margin;
      const z = q.dot(forward);
      distance = Math.max(distance, x / tanH - z, y / tanV - z);
    }
    return distance;
  }

  /**
   * Set goalPos/goalTgt for a game state and the current view. Views 0 and 3 frame the playfield with fitPlayView
   * (which recentres, and treats the DMD as a soft constraint); view 2 and the attract sweep look at the centre of
   * their fit points and back off far enough for the whole set to be on screen at the current aspect; view 1
   * follows the ball; INITIALS overrides every view with the DMD close-up.
   */
  computeGoal(state, world, now) {
    const attract = state === 'ATTRACT';
    const dir = this.tmpV;
    if (attract) {
      // Slow sweep around the whole machine.
      const bob = Math.sin(now * 0.16) * 0.5 + 0.5;
      const yaw = Math.sin(now * 0.1) * 0.5;
      const elevation = 0.36 + bob * 0.14;
      dir.set(Math.sin(yaw) * Math.cos(elevation), Math.sin(elevation), Math.cos(yaw) * Math.cos(elevation));
      this.goalTgt.copy(this.centreMachine);
      this.goalPos.copy(this.goalTgt).addScaledVector(dir, this.fitDistance(this.goalTgt, dir, this.fitMachine, 1.06));
    } else if (state === 'INITIALS') {
      // Entering initials happens entirely on the 128x32 DMD, so the camera goes to the panel: on the attract
      // sweep 2.5 m out the letter row the player is told to change is an unreadable smear.
      dir.set(0, 0.2, 1).normalize();
      this.goalTgt.copy(this.centreDmd);
      this.goalPos.copy(this.goalTgt).addScaledVector(dir, this.fitDistance(this.goalTgt, dir, this.fitDmd, 1.34));
    } else if (this.view === 3) {
      // Low wide shot with a gentle sway; the DMD is worth more of the frame here, so it gets more slack.
      const yaw = Math.sin(now * 0.22) * 0.08;
      dir.set(Math.sin(yaw) * 0.94, 0.34, Math.cos(yaw) * 0.94).normalize();
      this.fitPlayView(dir, 1.3);
    } else if (this.view === 2) {
      // Top-down: the whole 1.104 m playfield.
      dir.set(0, 1.12, 0.45).normalize();
      this.goalTgt.copy(this.centreTable);
      this.goalPos.copy(this.goalTgt).addScaledVector(dir, this.fitDistance(this.goalTgt, dir, this.fitTable, 1.03));
    } else if (this.view === 1) {
      // Follow the first active, unheld ball.
      const ball = world ? world.balls.find((b) => b.active && !b.held) : null;
      const bx = ball ? ball.p.x : 0.26;
      const by = ball ? ball.p.y : 0.4;
      this.fw(0.26 + (bx - 0.26) * 0.35, by - 0.42, 0.34, this.goalPos);
      this.fw(bx, by + 0.1, 0.02, this.goalTgt);
    } else {
      // Standard player view: the playfield is the subject; the DMD is only a soft constraint (fitPlayView).
      this.playDir(dir);
      this.fitPlayView(dir, 1.38);
    }
    return attract;
  }

  /**
   * View 0's direction. The elevation drops on wide frames and rises on tall ones: seen from a low camera the
   * pitched playfield presents a wide, short silhouette that fills a landscape frame, and from a high one a tall
   * narrow silhouette that fills a portrait frame. Without this the same fit distance leaves half the frame empty.
   */
  playDir(out) {
    const elevation = Math.max(0.42, Math.min(0.62, 0.72 - 0.16 * this.camera.aspect));
    return out.set(0, Math.sin(elevation), Math.cos(elevation));
  }

  /**
   * Framing for the player views. The playfield is fitted first and the DMD — which sits high on the backbox —
   * is only a soft constraint: the camera may back off by at most `slack` times the playfield-only distance to
   * bring it in, and beyond that the panel is allowed to sit partly out of frame. Fitting the union instead lets
   * the DMD's height set the distance, which pushes the machine to a third of the frame and makes the playfield
   * art illegible on wide aspects, where the surplus width is wasted anyway.
   */
  fitPlayView(dir, slack) {
    const table = this.frameFit(this.centreTable, dir, this.fitTable, 1.03, this.fitPlayfieldOnly);
    const cap = table.distance * slack;
    // The largest framing that stays inside the cap wins: the whole machine front (translite included) when the
    // frame is tall enough to carry it for free, otherwise the playfield plus the DMD.
    let full = this.frameFit(this.centreTable, dir, this.fitMachineFront, 1.04, this.fitWithDmd);
    if (full.distance > cap) full = this.frameFit(this.centreTable, dir, this.fitTableDmd, 1.04, this.fitWithDmd);
    let distance = full.distance;
    let ox = full.ox;
    let oy = full.oy;
    if (full.distance > cap) {
      // Too expensive: stop at the cap and take the fraction of the way toward the DMD framing that buys.
      const t = (cap - table.distance) / (full.distance - table.distance);
      distance = cap;
      ox = table.ox + (full.ox - table.ox) * t;
      oy = table.oy + (full.oy - table.oy) * t;
    }
    // Never crop the playfield: at `distance` these are exactly the offsets that keep every playfield point in
    // the frustum, so clamping here makes the soft DMD constraint safe whatever the blend did.
    ox = Math.min(Math.max(ox, table.ax - table.tanH * distance), table.bx + table.tanH * distance);
    oy = Math.min(Math.max(oy, table.ay - table.tanV * distance), table.by + table.tanV * distance);
    this.goalTgt.copy(this.centreTable).addScaledVector(this.tmpRight, ox).addScaledVector(this.tmpUp, oy);
    this.goalPos.copy(this.goalTgt).addScaledVector(dir, distance);
  }

  /**
   * Closed-form frame fit: the distance along `dir`, and the offset of the look-at point within the camera's
   * right/up plane, at which every point in `points` fits the frustum with a relative `margin` AND is centred in
   * it. Fitting alone is not enough — looking at the centroid of a pitched playfield puts the near edge hard
   * against the bottom of the frame while a fifth of the frame above it stays empty, so the camera has to back
   * off much further than the content actually needs.
   *
   * With a = q.right, b = q.up, c = q.forward for each point q = p - target, the point fits horizontally iff
   * |a - ox| <= tanH * (c + d); that is feasible iff d >= (max(a - tanH*c) - min(a + tanH*c)) / (2*tanH), and the
   * midpoint of those two bounds is the offset that centres the set. Same again vertically. Results are written
   * into `out` (reused every frame); `ax/bx/ay/by` are the bounds a caller needs to clamp a blended offset.
   * On return this.tmpRight / this.tmpUp hold the basis the offsets are expressed in.
   */
  frameFit(target, dir, points, margin, out) {
    const forward = this.tmpDir.copy(dir).negate();
    const right = this.tmpRight.crossVectors(forward, Renderer.UP);
    if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
    right.normalize();
    const up = this.tmpUp.crossVectors(right, forward);
    const tanV = Math.tan((this.camera.fov * Math.PI) / 360);
    const tanH = tanV * this.camera.aspect;
    let ax = -Infinity;
    let bx = Infinity;
    let ay = -Infinity;
    let by = Infinity;
    const q = this.tmpV2;
    for (const p of points) {
      q.copy(p).sub(target);
      const a = q.dot(right);
      const b = q.dot(up);
      const c = q.dot(forward);
      ax = Math.max(ax, a - tanH * c);
      bx = Math.min(bx, a + tanH * c);
      ay = Math.max(ay, b - tanV * c);
      by = Math.min(by, b + tanV * c);
    }
    out.distance = Math.max(0.2, (ax - bx) / (2 * tanH), (ay - by) / (2 * tanV)) * margin;
    out.ox = (ax + bx) / 2;
    out.oy = (ay + by) / 2;
    out.ax = ax;
    out.bx = bx;
    out.ay = ay;
    out.by = by;
    out.tanH = tanH;
    out.tanV = tanV;
    return out;
  }

  /** Pick the camera goal for the current mode/view, ease toward it, then add multiball kick and shake. */
  updateCamera(dt, world, game, now) {
    const attract = this.computeGoal(game.state, world, now);
    // Frame-rate independent easing; a long hitch (capped) must not snap the camera.
    const k = 1 - Math.exp(-(attract ? 1.2 : 6.5) * Math.min(dt, 0.1));
    this.camPos.lerp(this.goalPos, k);
    this.camTgt.lerp(this.goalTgt, k);
    if (attract) {
      // The attract sweep eases only its direction: the distance is re-fitted for the direction the camera is
      // actually looking from, so the slow lerp can never drift inside the framing and crop the machine.
      const offset = this.tmpOrbit.copy(this.camPos).sub(this.camTgt);
      if (offset.lengthSq() > 1e-8) {
        offset.normalize();
        const radius = this.fitDistance(this.camTgt, offset, this.fitMachine, 1.06);
        this.camPos.copy(this.camTgt).addScaledVector(offset, radius);
      }
    }
    if (this.camKick > 0) {
      this.camKick = Math.max(0, this.camKick - dt * 1.4);
      this.camPos.y += Math.sin(this.camKick * Math.PI) * 0.06;
    }
    this.shake.mag = Math.max(0, this.shake.mag - dt * 3.2);
    const shakeAmp = this.shake.mag ** 2 * 0.02;
    const shakeX = (Math.sin(now * 61) + this.shake.dx * 1.5) * shakeAmp;
    const shakeY = (Math.cos(now * 53) + 0.4) * shakeAmp * 0.7;
    this.camera.position.copy(this.camPos);
    this.camera.position.x += shakeX;
    this.camera.position.y += shakeY;
    this.tmpV.copy(this.camTgt);
    this.tmpV.x += shakeX * 0.4;
    this.camera.lookAt(this.tmpV);
  }

  /** Cycle standard -> follow -> top-down -> low wide. */
  cycleView() {
    this.view = (this.view + 1) % 4;
  }

  // ---- View Machine (orbit viewer) -------------------------------------------------------------------------------

  /**
   * Enter/leave the machine viewer. main.js pauses the simulation around it; the viewer runs its own animation
   * loop, orbits with pointer drag / wheel / pinch on the canvas, hides the registered DOM overlays and, on exit,
   * restores the gameplay camera and redraws the last gameplay frame even while the game stays paused.
   */
  setMachineView(active) {
    active = Boolean(active);
    const mv = this.machineView;
    if (active === mv.active) return;
    const canvas = this.renderer.domElement;
    if (active) {
      this.savedView = { pos: this.camPos.clone(), tgt: this.camTgt.clone(), view: this.view };
      mv.active = true;
      this.pointers.clear();
      this.scene.fog.near = this.fogMachine.near;
      this.scene.fog.far = this.fogMachine.far;
      this.machineDir(this.tmpV);
      mv.radius = this.fitDistance(this.machineTarget, this.tmpV, this.machineCorners, 1.08);
      for (const el of this.overlayElements) el.style.visibility = 'hidden';
      canvas.addEventListener('pointerdown', this.onPointerDown);
      canvas.addEventListener('pointermove', this.onPointerMove);
      canvas.addEventListener('pointerup', this.onPointerUp);
      canvas.addEventListener('pointercancel', this.onPointerUp);
      canvas.addEventListener('wheel', this.onWheel, { passive: false });
      this.renderMachineFrame();
      this.machineRaf = requestAnimationFrame(this.machineLoop);
    } else {
      mv.active = false;
      cancelAnimationFrame(this.machineRaf);
      this.machineRaf = 0;
      canvas.removeEventListener('pointerdown', this.onPointerDown);
      canvas.removeEventListener('pointermove', this.onPointerMove);
      canvas.removeEventListener('pointerup', this.onPointerUp);
      canvas.removeEventListener('pointercancel', this.onPointerUp);
      canvas.removeEventListener('wheel', this.onWheel);
      this.pointers.clear();
      this.scene.fog.near = this.fogPlay.near;
      this.scene.fog.far = this.fogPlay.far;
      for (const el of this.overlayElements) el.style.visibility = '';
      if (this.savedView) {
        this.camPos.copy(this.savedView.pos);
        this.camTgt.copy(this.savedView.tgt);
        this.view = this.savedView.view;
        this.savedView = null;
      }
      // Show the gameplay frame again even if main.js keeps the loop paused.
      const last = this.lastFrame;
      if (last.world && last.game) this.render(0, last.world, last.game, last.alpha, last.prev);
    }
  }

  /** Unit vector from the orbit target toward the camera for the current yaw/pitch. */
  machineDir(out) {
    const mv = this.machineView;
    const c = Math.cos(mv.pitch);
    return out.set(Math.sin(mv.yaw) * c, Math.sin(mv.pitch), Math.cos(mv.yaw) * c);
  }

  /**
   * Place the camera on the orbit around the machine. The drawn radius is additionally capped so the camera never
   * travels through one of the room's walls (the user's own zoom level in machineView.radius is left untouched).
   */
  updateMachineCamera() {
    const mv = this.machineView;
    mv.pitch = Math.max(0.12, Math.min(1.3, mv.pitch)); // never below ~7 deg: the underside is a closed box
    mv.radius = Math.max(0.8, Math.min(5, mv.radius));
    const dir = this.machineDir(this.tmpV);
    const room = Renderer.ROOM_HALF_Z - 0.35 - Math.abs(this.machineTarget.z);
    const radius = Math.abs(dir.z) > 1e-3 ? Math.min(mv.radius, room / Math.abs(dir.z)) : mv.radius;
    this.camera.position.copy(this.machineTarget).addScaledVector(dir, radius);
    this.camera.lookAt(this.machineTarget);
  }

  /** One frame of the viewer: orbit camera + the same post chain as gameplay (nothing else animates). */
  renderMachineFrame() {
    if (this.hostSuspended || document.hidden) return;
    this.updateMachineCamera();
    this.post.render(this.scene, this.camera, this.time);
  }

  machinePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
    try {
      this.renderer.domElement.setPointerCapture(event.pointerId);
    } catch (err) {
      /* capture is a convenience; dragging still works without it */
    }
    event.preventDefault();
  }

  machinePointerMove(event) {
    const pointer = this.pointers.get(event.pointerId);
    if (!pointer) return;
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    const mv = this.machineView;
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.pinchDist > 0 && dist > 0) mv.radius *= this.pinchDist / dist;
      this.pinchDist = dist;
    } else {
      mv.yaw -= dx * 0.008;
      mv.pitch += dy * 0.006;
    }
    event.preventDefault();
  }

  machinePointerUp(event) {
    this.pointers.delete(event.pointerId);
    this.pinchDist = 0;
  }

  machineWheel(event) {
    this.machineView.radius *= Math.exp(event.deltaY * 0.0012);
    event.preventDefault();
  }

  /** Release every GPU resource (geometries, materials, textures, render targets) and the WebGL context. */
  dispose() {
    this.setMachineView(false);
    const seen = new Set();
    const free = (resource) => {
      if (!resource || seen.has(resource) || typeof resource.dispose !== 'function') return;
      seen.add(resource);
      resource.dispose();
    };
    this.scene.traverse((node) => {
      free(node.geometry);
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      for (const material of materials) {
        if (!material) continue;
        for (const key of ['map', 'roughnessMap', 'emissiveMap', 'alphaMap']) free(material[key]);
        free(material);
      }
    });
    for (const visual of this.ballPool) free(visual.blob.material);
    free(this.ballGeo);
    free(this.ballMat);
    free(this.blobGeo);
    free(this.blobTex);
    free(this.scene.environment);
    free(this.dmdTex);
    free(this.transTex);
    free(this.miniScreenTex);
    this.post.dispose();
    this.renderer.dispose();
  }
}

/** World up axis shared by the camera fits. */
Renderer.UP = new THREE.Vector3(0, 1, 0);
/** Half depth of the room: the two walls sit at z = -/+ this, and the machine viewer never orbits past them.
 *  Far enough that the viewer's fitted orbit distance (widest in a narrow portrait window) still fits inside. */
Renderer.ROOM_HALF_Z = 3.6;
